import React, { useState, useEffect, useRef, useMemo } from "react";
import "./App.css";

/* Official KUNDA brand assets */
import kundaLogo  from "./assets/branding/kunda-logo.png";
import iconHome     from "./assets/categories/home.png";
import iconServices from "./assets/categories/services.png";
import navExplore  from "./assets/navigation/explore.png";
import navWishlist from "./assets/navigation/wishlist.png";
import navProfile  from "./assets/navigation/profile.png";

import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  serverTimestamp,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  ref as sRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification,
  reload,
  updatePassword,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
  deleteUser,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { db, storage, auth } from "./firebase";
import { IconArrowLeft, SubpageHeader, IconBed, IconBath, PhotoCarousel, FALLBACK_IMG, IconHomeLine } from "./ui";
import { compressImage } from "./imageUtils";
import iconLongTerm from "./assets/categories/longterm.png";
import { LongTermFeed, LongTermDetail, LongTermWizard, ListingTypeChooser, LtCard } from "./longterm";
import { RequestSheet, NotificationsPanel, RequestDetail, HostRequestsPanel, BellButton } from "./requests";

/* ─────────────────────────────────────────
   ICON PRELOAD
   Fetch every brand icon the moment the bundle parses — before first render.
   They're tiny, bundled, and cached immutably, so this is nearly free; it
   guarantees the logo, nav, and category icons appear instantly with no
   flash (including later surfaces like the auth screen and footer).
───────────────────────────────────────── */
[kundaLogo, iconHome, iconServices, navExplore, navWishlist, navProfile]
  .forEach(function(src) { var im = new Image(); im.src = src; });

/* ─────────────────────────────────────────
   FIREBASE
───────────────────────────────────────── */
/* Firebase instances now live in ./firebase (shared with longterm.js) */

/* KUNDA central WhatsApp number */
var KUNDA_WA = "2203952114";

/* ── EMAIL VERIFICATION SWITCH ──────────────────────────────
   Launch decision: the verification service is not reliable yet, so the
   REQUIREMENT is disabled. Nothing is deleted — every gate, the banner,
   resend and refresh all remain in the code and reactivate by setting
   this back to true once the service is fixed.

   This ONLY affects user email verification. The property review /
   approval process is entirely separate and is untouched: listings still
   submit as "pending" and only go live through the existing approval. */
var REQUIRE_EMAIL_VERIFICATION = false;

/* The verified state the app should act on. With the requirement disabled
   this is always true; with it enabled it reflects real Firebase Auth. */
function effectiveVerified(fbUser) {
  if (!REQUIRE_EMAIL_VERIFICATION) return true;
  return !!(fbUser && fbUser.emailVerified);
}

/* Firestore collections */
var LISTINGS_COL = "listings";
var USERS_COL    = "users";
var FEEDBACK_COL = "feedback";  /* report-a-problem + feature suggestions */
var WISHLIST_SUB = "wishlist"; /* subcollection on user docs */

/* localStorage key for guest-mode wishlist (carries over to account on signup) */
var GUEST_WISHLIST_KEY = "kunda_guest_wishlist";

/* localStorage key for the user's last app mode (guest/host) */
var APP_MODE_KEY = "kunda_app_mode";

/* Areas with their codes */
/* AREAS in priority order with unique marketing-grade section titles per spec.
   "Similar Areas" is a special catch-all section, only shown when orphans exist. */
var AREAS = [
  { name: "Senegambia",  code: "SG",  sectionTitle: "Popular Homes in" },
  { name: "Kololi",      code: "KL",  sectionTitle: "Trending Stays in" },
  { name: "Bijilo",      code: "BJ",  sectionTitle: "Beach Homes in" },
  { name: "Kotu",        code: "KT",  sectionTitle: "Best Short Stays in" },
  { name: "Sukuta",      code: "SK",  sectionTitle: "Affordable Homes in" },
  { name: "Brufut",      code: "BR",  sectionTitle: "Family Homes in" },
  { name: "Bakau",       code: "BK",  sectionTitle: "Verified Stays in" },
  { name: "Bakoteh",     code: "BKT", sectionTitle: "New Homes in" },
];
var SIMILAR_AREAS_KEY = "__similar__";  /* internal key for orphans bucket */

/* Locations offered in the Long-term search panel. Derived from AREAS so
   every row is a place KUNDA genuinely groups listings by — the list can
   never drift into filler, and it updates automatically when AREAS does. */
var SEARCH_LOCATIONS = AREAS.map(function(a) { return a.name; });

var PROPERTY_TYPES = [
  { id: "apartment",   label: "Apartment",   icon: "🏢" },
  { id: "house",       label: "House",       icon: "🏠" },
  { id: "studio",      label: "Studio",      icon: "🛋" },
  { id: "room",        label: "Room",        icon: "🚪" },
  { id: "villa",       label: "Villa",       icon: "🏡" },
  { id: "guest_house", label: "Guest House", icon: "🏘" },
  { id: "beach_home",  label: "Beach Home",  icon: "🏖" },
  { id: "other",       label: "Other",       icon: "✨" },
];

var AMENITIES = [
  { id: "wifi",         label: "WiFi",          icon: "📶" },
  { id: "ac",           label: "AC",            icon: "❄️" },
  { id: "water",        label: "Running Water", icon: "💧" },
  { id: "hot_water",    label: "Hot Water",     icon: "🚿" },
  { id: "kitchen",      label: "Kitchen",       icon: "🍳" },
  { id: "parking",      label: "Parking",       icon: "🅿️" },
  { id: "security",     label: "Security",      icon: "🛡️" },
  { id: "generator",    label: "Generator",     icon: "🔌" },
  { id: "tv",           label: "TV",            icon: "📺" },
  { id: "washer",       label: "Washer",        icon: "🧺" },
  { id: "balcony",      label: "Balcony",       icon: "🌅" },
  { id: "beach_access", label: "Beach Access",  icon: "🏝" },
];

/* KUNDA platform fee — 5% of nightly price.
   Update here if commercial terms change; used in wizard breakdown. */
var KUNDA_FEE_RATE = 0.05;

/* Local inline-SVG placeholder (KUNDA ivory + soft house mark).
   Zero network request — shows instantly if a photo ever fails to load.
   Replaces the old remote Unsplash fallback, which was slow, could itself
   fail, and misleadingly showed a random house that wasn't the property. */

/* Status checker — forgiving of typos like "approve" vs "approved".
   Returns true if the listing should be considered approved/visible. */
function isApprovedStatus(status) {
  if (!status) return false;
  var s = String(status).toLowerCase().trim();
  return s === "approved" || s === "approve" || s === "active" || s === "live";
}
function isPendingStatus(status) {
  if (!status) return false;
  var s = String(status).toLowerCase().trim();
  return s === "pending" || s === "review" || s === "in_review";
}
function isRejectedStatus(status) {
  if (!status) return false;
  var s = String(status).toLowerCase().trim();
  return s === "rejected" || s === "reject" || s === "denied";
}

/* ─────────────────────────────────────────
   ICONS
───────────────────────────────────────── */
function IconSearch() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>); }
function IconHeart(props) {
  if (props.filled) return (<svg viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="#FF385C" stroke="white" strokeWidth="1.8"/></svg>);
  return (<svg viewBox="0 0 24 24" fill="rgba(0,0,0,0.4)" stroke="white" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>);
}
function IconClose() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>); }
function IconPin() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>); }
function IconUsers() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>); }
function IconUser() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>); }
function IconDoc() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>); }
function IconLock() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>); }
function IconCheck() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>); }
function IconChevronR() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>); }
function IconHelp() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>); }
function IconEye() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>); }
function IconEyeOff() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>); }
function IconEdit() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>); }
function IconLogout() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>); }
function IconHostKey() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>); }
function IconInbox() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    </svg>
  );
}
function IconDashboard() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></svg>); }
function IconHomes() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12 L12 4 L21 12"/><path d="M5 10 V20 H19 V10"/><path d="M10 20 V14 H14 V20"/></svg>); }
function IconTrash() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>); }
function IconPlus() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>); }
function IconMinus() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>); }
function IconShare() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>); }
function IconVerified() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>); }
function IconWifi() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>); }

function WaIcon() {
  return (
    <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "white" }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.558 4.116 1.535 5.845L.057 23.557a.5.5 0 0 0 .612.612l5.712-1.478A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.96 0-3.791-.57-5.33-1.554l-.381-.232-3.392.877.893-3.263-.25-.398A9.953 9.953 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
    </svg>
  );
}

/* KUNDA Logo */


/* Animated home icon for category tab */
/* Realistic Architectural Villa Icon
   Modern Gambian villa: clean flat-ish roof, walls with shadow,
   warm glowing windows, real door that breathes (subtle scale animation),
   small chimney for character, ground line for grounding */
/* ─────────────────────────────────────────
   PHOTO CAROUSEL
───────────────────────────────────────── */

/* ─────────────────────────────────────────
   LISTING CARD (Airbnb style)
───────────────────────────────────────── */
/* Auto-generate location-based title for homepage cards.
   Custom host titles only appear on the DetailPage. */
/* Memoized: only re-render when the photo set, size, or start index changes —
   not when a parent (e.g. DetailPage) re-renders for the booking sheet/gallery. */

function autoTitle(propertyType, location) {
  var typeMap = {
    "apartment":   "Apartment in",
    "house":       "House in",
    "studio":      "Studio stay in",
    "room":        "Room in",
    "villa":       "Villa in",
    "guest house": "Guest house in",
    "guesthouse":  "Guest house in",
    "beach home":  "Beach stay in",
    "other":       "Stay in"
  };
  var pt = (propertyType || "").toLowerCase().trim();
  var prefix = typeMap[pt] || "Place to stay in";
  var loc = (location || "The Gambia").replace(/,\s*(The\s+)?Gambia\s*$/i, "").trim();
  if (!loc) loc = "The Gambia";
  return prefix + " " + loc;
}

function ListingCardBase(props) {
  var l = props.listing;
  /* Cards prefer tiny thumbnails; fall back to full photos for older listings */
  var thumbs = l.photoThumbs && l.photoThumbs.length > 0 ? l.photoThumbs : null;
  var photos = thumbs || (l.photos && l.photos.length > 0 ? l.photos : (l.imageUrl ? [l.imageUrl] : []));
  var fullFirst = (l.photos && l.photos[0]) || l.imageUrl || "";
  var compact = !!props.compact;
  var [pop, setPop] = useState(false);

  /* Predictive preload — warm the full hero image the instant a finger lands,
     before the tap registers, so the property page hero is already cached. */
  function preloadHero() {
    if (fullFirst) { var im = new Image(); im.src = fullFirst; }
  }

  function handleHeart(e) {
    e.stopPropagation();
    if (!props.saved) {            /* pop only when saving, not removing */
      setPop(true);
      setTimeout(function() { setPop(false); }, 360);
    }
    props.onSave(l.id);
  }

  return (
    <div className={"card" + (compact ? " c-compact" : "")}
         onClick={function() { props.onTap(l); }}
         onTouchStart={preloadHero}
         onMouseDown={preloadHero}>
      <PhotoCarousel photos={photos}/>
      {props.featured && <span className="c-featured">★ Featured</span>}
      <button className={"c-heart" + (pop ? " pop" : "")} onClick={handleHeart} aria-label={props.saved ? "Remove from saved" : "Save"}>
        <IconHeart filled={props.saved}/>
      </button>

      <div className="c-body">
        <div className="c-auto-title">{autoTitle(l.propertyType, l.location)}</div>
        <div className="c-price">
          <strong>GMD {Number(l.pricePerNight).toLocaleString()}</strong>
          <span> / night</span>
        </div>
        {!compact && l.guests ? (
          <div className="c-capacity">{l.guests} {Number(l.guests) === 1 ? "guest" : "guests"}</div>
        ) : null}
        {!compact && (
          <div className="c-meta-row">
            {l.code && <span className="c-code">{l.code}</span>}
            <span className="c-verified"><IconVerified/> Verified</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SkeletonCardBase() {
  return (
    <div className="skel">
      <div className="skel-img"/>
      <div className="skel-line"/>
      <div className="skel-line short"/>
      <div className="skel-line" style={{ width: "45%" }}/>
    </div>
  );
}

/* Memoized cards — the single biggest win. When a save toggles or search
   updates, only the card whose data actually changed re-renders. Function
   props (onTap/onSave) are intentionally ignored; we compare the data. */
var ListingCard = React.memo(ListingCardBase, function(a, b) {
  return a.listing === b.listing && a.saved === b.saved && a.featured === b.featured && a.compact === b.compact;
});
var SkeletonCard = React.memo(SkeletonCardBase);

/* ─────────────────────────────────────────
   EXPLORE PAGE
───────────────────────────────────────── */
/* Recently-viewed properties — stored in localStorage, no Firebase.
   Max 20, newest first, no duplicates (re-viewing moves to top). */
var RECENT_KEY = "kunda_recent_v1";
var CONTINUE_VIEW = "__continue__";

/* ── Deep-link URL helpers — every property has a permanent /property/{id} URL ── */
var SITE_URL = "https://kundastays.com";
function parsePropertyId() {
  var m = window.location.pathname.match(/^\/property\/([^/]+)\/?$/);
  return m ? decodeURIComponent(m[1]) : null;
}
function pushPropertyUrl(id) {
  try { window.history.pushState({ property: id }, "", "/property/" + encodeURIComponent(id)); } catch (e) {}
}
function pushHomeUrl() {
  try {
    if (window.location.pathname !== "/") window.history.pushState({}, "", "/");
  } catch (e) {}
}
/* In-memory cache: ExplorePage reads this every render (including every search
   keystroke), so we parse localStorage once and invalidate on writes instead
   of re-parsing JSON dozens of times per session. */
var recentViewsCache = null;
function getRecentViews() {
  if (recentViewsCache !== null) return recentViewsCache;
  try {
    var raw = (typeof localStorage !== "undefined") ? localStorage.getItem(RECENT_KEY) : null;
    if (!raw) { recentViewsCache = []; return recentViewsCache; }
    var arr = JSON.parse(raw);
    recentViewsCache = Array.isArray(arr) ? arr : [];
    return recentViewsCache;
  } catch (e) { recentViewsCache = []; return recentViewsCache; }
}
function addRecentView(l) {
  if (!l || !l.id) return;
  try {
    var arr = getRecentViews().filter(function(x) { return x.id !== l.id; });
    arr.unshift({
      id: l.id,
      title: l.title || "",
      propertyType: l.propertyType || "",
      location: l.location || "",
      pricePerNight: l.pricePerNight || 0,
      code: l.code || "",
      guests: l.guests || 0,
      image: (l.photos && l.photos[0]) || l.imageUrl || "",
      ts: Date.now()
    });
    if (arr.length > 20) arr = arr.slice(0, 20);
    recentViewsCache = arr;
    localStorage.setItem(RECENT_KEY, JSON.stringify(arr));
  } catch (e) {}
}

/* Pure helpers at module scope — stable identity, safe for useMemo deps */
function matchHypeArea(loc) {
  if (!loc) return null;
  var clean = String(loc).replace(/,\s*(the\s+)?gambia$/i, "").trim();
  var cleanLower = clean.toLowerCase();
  for (var i = 0; i < AREAS.length; i++) {
    if (AREAS[i].name.toLowerCase() === cleanLower) return AREAS[i].name;
  }
  return null;
}

function ExplorePage(props) {
  var [q, setQ] = useState("");
  /* Debounced query — typing updates `q` instantly (responsive input), but the
     heavy filtering only runs ~90ms after you stop typing. No mid-keystroke jank. */
  var [qApplied, setQApplied] = useState("");
  var [searchOpen, setSearchOpen] = useState(false);

  useEffect(function() {
    var t = setTimeout(function() { setQApplied(q); }, 90);
    return function() { clearTimeout(t); };
  }, [q]);
  var [typeFilter, setTypeFilter] = useState("all");
  var [priceFilter, setPriceFilter] = useState("any");   /* any | u2000 | u5000 | u10000 | o10000 */
  var [guestFilter, setGuestFilter] = useState("any");   /* any | 1 | 2 | 4 | 6 */
  var [amenFilter, setAmenFilter] = useState([]);        /* array of amenity ids */
  var [filterOpen, setFilterOpen] = useState(false);
  var [seeAllArea, setSeeAllArea] = useState(null);  /* null = home, "Bakau" = vertical feed for Bakau */
  /* Category is owned by App so it survives the detail-page unmount —
     ExplorePage is destroyed every time a listing opens. */
  var category = props.category;
  function setCategory(c) { props.onCategory(c); }

  /* Long-term location panel. NOTE: this must sit AFTER the category state
     above — `var` hoisting means reading `category` any earlier gives
     undefined, which silently pinned isLongTerm to false and stopped the
     panel from ever opening. */
  var isLongTerm = category === "long_term";
  var ltSearchRef = useRef(null);

  function openSearch() { if (isLongTerm) setSearchOpen(true); }
  /* Cancel backs all the way out: panel shut, scrim gone, text cleared. */
  function cancelSearch() {
    setQ("");
    setSearchOpen(false);
  }
  function chooseLocation(name) {
    setQ(name);
    setSearchOpen(false);
  }
  /* Never leave the panel hanging open after a mode switch */
  useEffect(function() { if (!isLongTerm) setSearchOpen(false); }, [isLongTerm]);
  /* autoFocus only fires on mount; the field is already mounted, so focus
     it explicitly once the panel opens. */
  useEffect(function() {
    if (!searchOpen || !isLongTerm) return;
    var t = setTimeout(function() {
      if (ltSearchRef.current) ltSearchRef.current.focus();
    }, 80);
    return function() { clearTimeout(t); };
  }, [searchOpen, isLongTerm]);

  /* Rows filter live as the person types — case-insensitive, matches anywhere */
  var ltFiltered = SEARCH_LOCATIONS.filter(function(n) {
    return n.toLowerCase().indexOf(q.trim().toLowerCase()) >= 0;
  });

  /* Sticky search — scroll-linked progressive animation.
     - `stuck` boolean: toggles binary states (background, shadow) at threshold
     - .is-stuck class: drives the one-shot icon hide/show transition
     Using rAF + ref keeps this at 60fps without re-renders on every scroll frame. */
  var [stuck, setStuck] = useState(false);
  var stickyRef = useRef(null);
  useEffect(function() {
    var ticking = false;
    var lastStuckState = false;
    function update() {
      var y = window.scrollY;
      /* Two-state header: the Home/Services icons hide/show via a single CSS
         transition on .is-stuck (no per-frame scroll-linked values — those
         step and snap on iOS where scroll events arrive sparsely). Hysteresis
         (sticks at 28px, releases below 8px) prevents any flip-flop jitter
         when resting near the threshold. */
      var nowStuck = lastStuckState ? (y > 8) : (y > 28);
      if (nowStuck !== lastStuckState) {
        lastStuckState = nowStuck;
        setStuck(nowStuck);
      }
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return function() { window.removeEventListener("scroll", onScroll); };
  }, []);

  /* Filter by search query first — matches title, area, code, AND property type */
  var searched = useMemo(function() {
    var qq = qApplied.toLowerCase().trim();
    return props.listings.filter(function(l) {
      if (l.rentalType === "long_term") return false;   /* Long-Term has its own feed */
      if (!qq) return true;
      var typeObj = PROPERTY_TYPES.find(function(t) { return t.id === l.propertyType; });
      var typeLabel = typeObj ? typeObj.label : "";
      var text = ((l.title || "") + " " + (l.location || "") + " " + (l.code || "") +
                  " " + typeLabel + " " + (l.propertyType || "")).toLowerCase();
      return text.includes(qq);
    });
  }, [props.listings, qApplied]);

  /* Then apply the quick type filter chip + advanced filters */
  var filtered = useMemo(function() {
    return searched.filter(function(l) {
      if (typeFilter !== "all" && l.propertyType !== typeFilter) return false;

      /* Price */
      var p = Number(l.pricePerNight) || 0;
      if (priceFilter === "u2000" && !(p < 2000)) return false;
      if (priceFilter === "u5000" && !(p < 5000)) return false;
      if (priceFilter === "u10000" && !(p < 10000)) return false;
      if (priceFilter === "o10000" && !(p >= 10000)) return false;

      /* Guests */
      if (guestFilter !== "any") {
        var cap = Number(l.guests) || 0;
        var need = Number(guestFilter);
        if (cap < need) return false;
      }

      /* Amenities — listing must have ALL selected amenities */
      if (amenFilter.length > 0) {
        var la = l.amenities || [];
        for (var i = 0; i < amenFilter.length; i++) {
          if (la.indexOf(amenFilter[i]) === -1) return false;
        }
      }
      return true;
    });
  }, [searched, typeFilter, priceFilter, guestFilter, amenFilter]);

  /* Count of active advanced filters (for the button badge) */
  var advCount = (priceFilter !== "any" ? 1 : 0) + (guestFilter !== "any" ? 1 : 0) + amenFilter.length;

  /* Are we in "results" mode (search text or any filter active)? */
  var isFiltering = (qApplied && qApplied.trim().length > 0) || typeFilter !== "all" || advCount > 0;

  function toggleAmen(id) {
    setAmenFilter(function(prev) {
      return prev.indexOf(id) === -1 ? prev.concat([id]) : prev.filter(function(x) { return x !== id; });
    });
  }
  function clearAllFilters() {
    setTypeFilter("all"); setPriceFilter("any"); setGuestFilter("any"); setAmenFilter([]); setQ("");
  }

  /* Group listings: hype areas by canonical name, orphans into "Similar Areas" bucket */
  /* Featured (admin-marked) + Continue Exploring (recently viewed) */
  var featuredListings = useMemo(function() {
    return props.listings.filter(function(l) { return l.featured === true && l.rentalType !== "long_term"; });
  }, [props.listings]);

  /* Recently viewed — read fresh each render so it updates after returning
     from a property page. Map to live listings (skip any no longer available). */
  var recentViews = getRecentViews();
  var recentListings = [];
  recentViews.forEach(function(rv) {
    var live = props.listings.find(function(l) { return l.id === rv.id; });
    if (live) recentListings.push(live);
  });

  var byArea = useMemo(function() {
    var grouped = {};
    AREAS.forEach(function(a) { grouped[a.name] = []; });
    grouped[SIMILAR_AREAS_KEY] = [];
    searched.forEach(function(l) {
      var matched = matchHypeArea(l.location);
      if (matched) grouped[matched].push(l);
      else grouped[SIMILAR_AREAS_KEY].push(l);
    });
    return grouped;
  }, [searched]);

  /* Build the FINAL section order per user spec:
     1. Senegambia (always top)
     2. Similar Areas (only if it has listings)
     3-9. Remaining 7 hype areas in spec order
  */
  var sectionOrder = ["Senegambia"];
  if (byArea[SIMILAR_AREAS_KEY].length > 0) {
    sectionOrder.push(SIMILAR_AREAS_KEY);
  }
  AREAS.forEach(function(a) {
    if (a.name !== "Senegambia") sectionOrder.push(a.name);
  });

  /* Helper to get section display title */
  function getSectionTitle(key) {
    if (key === SIMILAR_AREAS_KEY) return "Homes in Similar Areas";
    var area = AREAS.find(function(a) { return a.name === key; });
    if (!area) return "Homes in " + key;
    return area.sectionTitle + " " + area.name;
  }

  /* SEE ALL — Continue Exploring (recently viewed) */
  if (seeAllArea === CONTINUE_VIEW) {
    var recentForView = recentListings.filter(function(l) {
      if (!qApplied) return true;
      var text = ((l.title || "") + " " + (l.location || "") + " " + (l.code || "")).toLowerCase();
      return text.includes(qApplied.toLowerCase().trim());
    });
    return (
      <div className="explore">
        <div className={"search-sticky" + (stuck ? " is-stuck" : "")}>
          <div className="search-shell">
            <div className="search-pill">
              <button className="search-back" onClick={function() { setSeeAllArea(null); setQ(""); }}>
                <IconArrowLeft/>
              </button>
              <input
                placeholder="Search your recent views"
                value={q}
                onChange={function(e) { setQ(e.target.value); }}
              />
            </div>
          </div>
        </div>

        <div className="seeall-title-row">
          <h1 className="seeall-title">Continue <em>exploring</em></h1>
          <p className="seeall-sub">Pick up where you left off.</p>
        </div>

        <div className="main">
          {recentForView.length > 0 ? (
            <div className="grid">
              {recentForView.map(function(l, i) {
                return (
                  <div key={l.id} className="card-fade" style={{ animationDelay: (i * 0.06) + "s" }}>
                    <ListingCard
                      listing={l}
                      onTap={props.onTap}
                      saved={props.saved.includes(l.id)}
                      onSave={props.onSave}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty">
              <div className="empty-emoji">🧭</div>
              <h3>Nothing here yet</h3>
              <p>Homes you view will appear here so you can pick up where you left off.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* SEE ALL VIEW — vertical feed for one area, with area tab switcher */
  if (seeAllArea !== null) {
    var listingsForArea = byArea[seeAllArea] || [];
    var isSimilarView = seeAllArea === SIMILAR_AREAS_KEY;
    var displayName = isSimilarView ? "Similar Areas" : seeAllArea;
    var fullTitle = getSectionTitle(seeAllArea);
    /* Split for italic styling */
    var fullTitlePrefix = fullTitle;
    var fullTitleArea = "";
    if (!isSimilarView) {
      var lastSpace = fullTitle.lastIndexOf(" ");
      fullTitlePrefix = fullTitle.substring(0, lastSpace);
      fullTitleArea = fullTitle.substring(lastSpace + 1);
    }

    return (
      <div className="explore">
        {/* Sticky search */}
        <div className={"search-sticky" + (stuck ? " is-stuck" : "")}>
          <div className="search-shell">
            <div className="search-pill">
              <button className="search-back" onClick={function() { setSeeAllArea(null); }}>
                <IconArrowLeft/>
              </button>
              <input
                placeholder={"Search in " + displayName}
                value={q}
                onChange={function(e) { setQ(e.target.value); }}
              />
            </div>
          </div>
        </div>

        {/* Area title */}
        <div className="seeall-title-row">
          <h1 className="seeall-title">
            {isSimilarView
              ? fullTitle
              : <>{fullTitlePrefix} <em>{fullTitleArea}</em></>}
          </h1>
        </div>

        {/* Area tabs — Senegambia first, Similar Areas second (if has items), then rest */}
        <div className="area-tabs-wrap">
          <div className="area-tabs">
            {sectionOrder.map(function(key) {
              var isActive = key === seeAllArea;
              var count = (byArea[key] || []).length;
              var label = key === SIMILAR_AREAS_KEY ? "Similar" : key;
              return (
                <button
                  key={key}
                  className={"area-tab" + (isActive ? " active" : "")}
                  onClick={function() { setSeeAllArea(key); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                >
                  {label}
                  {count > 0 && <span className="area-tab-count">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Vertical listings */}
        <div className="main">
          {listingsForArea.length > 0 ? (
            <div className="grid">
              {listingsForArea.map(function(l, i) {
                return (
                  <div key={l.id} className="card-fade" style={{ animationDelay: (i * 0.06) + "s" }}>
                    <ListingCard
                      listing={l}
                      onTap={props.onTap}
                      saved={props.saved.includes(l.id)}
                      onSave={props.onSave}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty">
              <div className="empty-emoji">🏡</div>
              <h3>Be among the first homes in {displayName}</h3>
              <p>List your place now and get featured when KUNDA grows in this area — early hosts stand out most.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* HOMEPAGE — horizontal sections per area */
  return (
    <div className="explore">
      {/* Sticky search + category toggle — both stay visible on scroll.
          Icons hide/show via a single .is-stuck CSS transition — no scroll-linked values. */}
      <div ref={stickyRef} className={"search-sticky" + (stuck ? " is-stuck" : "")}>
        <div className="search-shell">
          <div className={(isLongTerm && searchOpen) ? "search-row-open" : ""}>
          <div className={"search-pill" + (searchOpen ? " is-open" : "")}
               onClick={isLongTerm ? openSearch : null}>
            <IconSearch/>
            {isLongTerm ? (
              /* LONG-TERM — tapping opens the location panel below. readOnly so
                 the phone keyboard doesn't fight the panel for the screen. */
              <input
                ref={ltSearchRef}
                placeholder={searchOpen ? "Search destinations" : "Where do you want to live?"}
                value={q}
                onFocus={openSearch}
                onChange={function(e) { setQ(e.target.value); }}
              />
            ) : (
              /* SHORT STAYS — unchanged free-text search: property codes
                 (GMD-6903), names, locations, property types. */
              <input
                placeholder="Start your search"
                value={q}
                onChange={function(e) { setQ(e.target.value); }}
              />
            )}
            {q ? (
              <button className="search-clear" aria-label="Clear search"
                      onClick={function(e) { e.stopPropagation(); setQ(""); }}>
                <IconClose/>
              </button>
            ) : null}
          </div>
          {isLongTerm && searchOpen && (
            <button className="search-cancel-btn" onClick={cancelSearch}>Cancel</button>
          )}
          </div>
        </div>

        {/* LONG-TERM location panel — expands in place, directly under the
            search bar. Deliberately NOT a route or full-screen overlay: the
            home screen stays put underneath and simply dims. */}
        {isLongTerm && searchOpen && (
          <div className="ltloc">
            <div className="ltloc-label">Popular locations</div>
            <div className="ltloc-list">
              {ltFiltered.length === 0 ? (
                <div className="ltloc-none">
                  No matching areas — try Senegambia, Kololi, Kotu…
                </div>
              ) : ltFiltered.map(function(name) {
                return (
                  <button key={name} className="ltloc-row"
                          onClick={function() { chooseLocation(name); }}>
                    <span className="ltloc-ico"><IconPin/></span>
                    <span className="ltloc-name">{name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Marketplace modes — pinned TOGETHER with the search bar.
            Nothing animates on scroll: both simply stay put while the
            logo/profile bar and the content scroll away underneath.
            position:sticky reserves its own flow space, so no layout jump. */}
        <div className="cat-toggle-wrap">
          <div className="catp-row">
            <button className={"catp-item" + (category === "home" ? " on" : "")}
                    onClick={function() { setCategory("home"); }}>
              <img src={iconHome} alt="" className="catp-icon"/>
              <span className="catp-label">Short stays</span>
            </button>
            <button className={"catp-item" + (category === "long_term" ? " on" : "")}
                    onClick={function() { setCategory("long_term"); }}>
              <img src={iconLongTerm} alt="" className="catp-icon"/>
              <span className="catp-label">Long-term</span>
            </button>
            <button className={"catp-item" + (category === "services" ? " on" : "")}
                    onClick={function() { setCategory("services"); }}>
              <img src={iconServices} alt="" className="catp-icon"/>
              <span className="catp-label">Services</span>
            </button>
          </div>
        </div>
      </div>

      {/* CONNECTION ERROR — shown instead of a marketplace feed. Services stays
          reachable because it needs no Firestore data. */}
      {props.loadError && category !== "services" && (
        <div className="main">
          <div className="empty">
            <div className="empty-emoji">🌿</div>
            <h3>Couldn't load homes</h3>
            <p>Check your connection and try again — your saved homes are safe.</p>
            <button className="btn-primary" onClick={props.onRetry}>Try again</button>
          </div>
        </div>
      )}

      {isLongTerm && searchOpen && (
        <div className="ltloc-scrim" onClick={cancelSearch}/>
      )}

      {/* No key here on purpose. key={category} forced a full unmount +
         remount of every card on each switch — that was the "reload flash"
         and the jump. React now reconciles instead, so switching is
         instant. The data was always already in memory. */}
      <div className="cat-panel">
      {/* LONG-TERM TAB — dedicated rental feed */}
      {category === "long_term" && !props.loadError && (
        <LongTermFeed
          listings={props.listings}
          loading={props.loading}
          q={q}
          saved={props.saved}
          onSave={props.onSave}
          onTap={props.onTap}
          onBecomeHost={function() { props.onBecomeHost && props.onBecomeHost(); }}
        />
      )}

      {/* SERVICES TAB — coming soon splash */}
      {category === "services" && (
        <div className="services-splash">
          <div className="services-emoji">🏺</div>
          <h2 className="services-title">Services <em>coming soon</em></h2>
          <p className="services-sub">
            Curated experiences, transfers, and trusted local services — designed for travelers exploring The Gambia. Be among the first to know when we launch.
          </p>
          <button className="services-cta" onClick={function() { setCategory("home"); }}>
            Browse Homes
          </button>
        </div>
      )}

      {/* HOME TAB — current marketplace */}
      {category === "home" && !props.loadError && (
        <div className="qf-row">
          <button className={"qf-filters-btn" + (advCount > 0 ? " on" : "")}
                  onClick={function() { setFilterOpen(true); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/></svg>
            Filters{advCount > 0 ? " · " + advCount : ""}
          </button>
          <div className="qf-divider"/>
          <button className={"qf-chip" + (typeFilter === "all" ? " on" : "")}
                  onClick={function() { setTypeFilter("all"); }}>All</button>
          {PROPERTY_TYPES.filter(function(t) { return t.id !== "other"; }).map(function(t) {
            return (
              <button key={t.id}
                      className={"qf-chip" + (typeFilter === t.id ? " on" : "")}
                      onClick={function() { setTypeFilter(t.id); }}>
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* HOME TAB content */}
      {category === "home" && !props.loadError && (props.loading ? (
        <div className="main">
          <div className="grid">
            {[1,2,3,4,5,6,7,8].map(function(i) { return <SkeletonCard key={i}/>; })}
          </div>
        </div>
      ) : isFiltering ? (
        /* ── FLAT RESULTS VIEW — search or type filter active ── */
        <div className="main">
          {filtered.length > 0 ? (
            <>
              <div className="results-count">
                {filtered.length} {filtered.length === 1 ? "home" : "homes"} found
              </div>
              <div className="grid">
                {filtered.map(function(l) {
                  return (
                    <ListingCard
                      key={l.id}
                      listing={l}
                      onTap={props.onTap}
                      saved={props.saved.includes(l.id)}
                      onSave={props.onSave}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <div className="empty">
              <div className="empty-emoji">🔍</div>
              <h3>No homes found yet</h3>
              <p>Try another area or explore nearby locations.</p>
              <button className="btn-primary" onClick={clearAllFilters}>
                Clear filters
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="hp-sections">
          {/* CONTINUE EXPLORING — recently viewed (returning guests book these). First section. */}
          {recentListings.length > 0 && (
            <section className="hp-section">
              <div className="hp-section-header">
                <h2 className="hp-section-title">Continue <em>exploring</em></h2>
                <button className="hp-section-link" onClick={function() {
                  setSeeAllArea(CONTINUE_VIEW);
                  window.scrollTo(0, 0);
                }}>
                  See all
                  <IconChevronR/>
                </button>
              </div>
              <div className="hp-row hp-row-compact">
                {recentListings.slice(0, 8).map(function(l) {
                  return (
                    <div key={l.id} className="hp-row-item hp-row-item-sm">
                      <ListingCard listing={l} compact onTap={props.onTap}
                                   saved={props.saved.includes(l.id)} onSave={props.onSave}/>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* FEATURED — admin-marked premium/partner listings */}
          {featuredListings.length > 0 && (
            <section className="hp-section">
              <div className="hp-section-header">
                <h2 className="hp-section-title">Featured <em>homes</em></h2>
              </div>
              <div className="hp-row">
                {featuredListings.slice(0, 8).map(function(l) {
                  return (
                    <div key={l.id} className="hp-row-item">
                      <ListingCard listing={l} onTap={props.onTap}
                                   saved={props.saved.includes(l.id)} onSave={props.onSave} featured/>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {sectionOrder.map(function(sectionKey) {
            var areaListings = byArea[sectionKey] || [];
            var sectionTitle = getSectionTitle(sectionKey);
            var isSimilar = sectionKey === SIMILAR_AREAS_KEY;
            var hasListings = areaListings.length > 0;

            /* Split title into prefix and area name for italic styling
               e.g. "Popular Homes in Senegambia" → "Popular Homes in" + "Senegambia" */
            var titlePrefix = sectionTitle;
            var titleArea = "";
            if (!isSimilar) {
              var lastSpace = sectionTitle.lastIndexOf(" ");
              titlePrefix = sectionTitle.substring(0, lastSpace);
              titleArea = sectionTitle.substring(lastSpace + 1);
            }

            return (
              <section key={sectionKey} className="hp-section">
                <div className="hp-section-header">
                  <h2 className="hp-section-title">
                    {isSimilar
                      ? sectionTitle
                      : <>{titlePrefix} <em>{titleArea}</em></>}
                    {hasListings && (
                      <span className="hp-section-count">
                        {areaListings.length} {areaListings.length === 1 ? "home available" : "homes available"}
                      </span>
                    )}
                  </h2>
                  {hasListings && (
                    <button className="hp-section-link" onClick={function() {
                      setSeeAllArea(sectionKey);
                      window.scrollTo(0, 0);
                    }}>
                      See all
                      <IconChevronR/>
                    </button>
                  )}
                </div>

                {hasListings ? (
                  <div className="hp-row">
                    {areaListings.slice(0, 6).map(function(l) {
                      return (
                        <div key={l.id} className="hp-row-item">
                          <ListingCard
                            listing={l}
                            onTap={props.onTap}
                            saved={props.saved.includes(l.id)}
                            onSave={props.onSave}
                          />
                        </div>
                      );
                    })}
                    {/* See All card with stacked images */}
                    <div className="hp-row-item">
                      <SeeAllCard
                        areaName={isSimilar ? "Similar Areas" : sectionKey}
                        listings={areaListings}
                        onTap={function() {
                          setSeeAllArea(sectionKey);
                          window.scrollTo(0, 0);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  /* Coming Soon marketing card for empty sections (Option C) */
                  <div className="hp-row">
                    <div className="hp-row-item-wide">
                      <ComingSoonCard
                        areaName={sectionKey}
                        onTap={function() { props.onBecomeHost && props.onBecomeHost(); }}
                      />
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      ))}
      </div>

      {/* ── FILTER SHEET — price / guests / amenities ── */}
      {filterOpen && (
        <div className="bsheet-overlay" onClick={function() { setFilterOpen(false); }}>
          <div className="bsheet fsheet" onClick={function(e) { e.stopPropagation(); }}>
            <div className="bsheet-handle"/>
            <button className="bsheet-close" onClick={function() { setFilterOpen(false); }} aria-label="Close">
              <IconClose/>
            </button>

            <h2 className="bsheet-title">Filters</h2>

            {/* Price */}
            <div className="fsheet-group">
              <div className="fsheet-label">Price per night</div>
              <div className="fsheet-opts">
                {[["any","Any"],["u2000","Under 2,000"],["u5000","Under 5,000"],["u10000","Under 10,000"],["o10000","10,000+"]].map(function(o) {
                  return (
                    <button key={o[0]}
                            className={"fsheet-opt" + (priceFilter === o[0] ? " on" : "")}
                            onClick={function() { setPriceFilter(o[0]); }}>
                      {o[1]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Guests */}
            <div className="fsheet-group">
              <div className="fsheet-label">Guests</div>
              <div className="fsheet-opts">
                {[["any","Any"],["1","1+"],["2","2+"],["4","4+"],["6","6+"]].map(function(o) {
                  return (
                    <button key={o[0]}
                            className={"fsheet-opt" + (guestFilter === o[0] ? " on" : "")}
                            onClick={function() { setGuestFilter(o[0]); }}>
                      {o[1]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Amenities */}
            <div className="fsheet-group">
              <div className="fsheet-label">Amenities</div>
              <div className="fsheet-opts">
                {["wifi","parking","beach_access","kitchen","security","ac"].map(function(id) {
                  var a = AMENITIES.find(function(x) { return x.id === id; });
                  if (!a) return null;
                  return (
                    <button key={id}
                            className={"fsheet-opt" + (amenFilter.indexOf(id) !== -1 ? " on" : "")}
                            onClick={function() { toggleAmen(id); }}>
                      {a.icon} {a.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="fsheet-actions">
              <button className="fsheet-clear" onClick={clearAllFilters}>Clear all</button>
              <button className="fsheet-apply" onClick={function() { setFilterOpen(false); }}>
                Show {filtered.length} {filtered.length === 1 ? "home" : "homes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* See All card — 3 stacked images preview */
function SeeAllCardBase(props) {
  var imgs = [];
  for (var i = 0; i < Math.min(3, props.listings.length); i++) {
    var l = props.listings[i];
    imgs.push((l.photos && l.photos[0]) || l.imageUrl || FALLBACK_IMG);
  }
  while (imgs.length < 3) imgs.push(FALLBACK_IMG);

  return (
    <div className="see-all-card" onClick={props.onTap}>
      <div className="sa-stack">
        <div className="sa-img sa-img-3" style={{ backgroundImage: "url(" + imgs[2] + ")" }}/>
        <div className="sa-img sa-img-2" style={{ backgroundImage: "url(" + imgs[1] + ")" }}/>
        <div className="sa-img sa-img-1" style={{ backgroundImage: "url(" + imgs[0] + ")" }}/>
      </div>
      <div className="sa-label">
        <span>See all</span>
        <span className="sa-count">{props.listings.length} home{props.listings.length !== 1 ? "s" : ""}</span>
      </div>
    </div>
  );
}

/* Coming Soon marketing card — empty section CTA for hosts */
/* Memoized: re-render only when the area data changes, not on parent updates */
var SeeAllCard = React.memo(SeeAllCardBase, function(a, b) {
  return a.listings === b.listings && a.areaName === b.areaName && a.count === b.count;
});

function ComingSoonCardBase(props) {
  return (
    <div className="cs-card" onClick={props.onTap}>
      <div className="cs-inner">
        <div className="cs-emoji">✨</div>
        <div className="cs-title">Be among the first in <em>{props.areaName}</em></div>
        <div className="cs-sub">List your home now and get featured when KUNDA launches in this area.</div>
        <div className="cs-cta">
          List your home <IconChevronR/>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   FULL-SCREEN PHOTO GALLERY
   Immersive viewer: full photos on black, swipe, counter, close.
───────────────────────────────────────── */
var ComingSoonCard = React.memo(ComingSoonCardBase, function(a, b) {
  return a.areaName === b.areaName;
});

function FullScreenGallery(props) {
  var photos = props.photos && props.photos.length > 0 ? props.photos : [FALLBACK_IMG];
  var [idx, setIdx] = useState(props.startIndex || 0);
  var [touchStart, setTouchStart] = useState(null);
  var [dragX, setDragX] = useState(0);

  function onTouchStart(e) { setTouchStart(e.touches[0].clientX); setDragX(0); }
  function onTouchMove(e) {
    if (touchStart === null) return;
    var diff = e.touches[0].clientX - touchStart;
    if ((idx === 0 && diff > 0) || (idx === photos.length - 1 && diff < 0)) diff = diff * 0.35;
    setDragX(diff);
  }
  function onTouchEnd(e) {
    if (touchStart === null) return;
    var diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && idx < photos.length - 1) setIdx(idx + 1);
      else if (diff < 0 && idx > 0) setIdx(idx - 1);
    }
    setTouchStart(null);
    setDragX(0);
  }
  function handleErr(e) { e.target.src = FALLBACK_IMG; }

  var trackStyle = {
    transform: "translateX(calc(" + (-idx * 100) + "% + " + dragX + "px))",
    transition: touchStart === null ? "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)" : "none"
  };

  return (
    <div className="fsg">
      <div className="fsg-top">
        <div className="fsg-counter">{(idx + 1) + " / " + photos.length}</div>
        <button className="fsg-close" onClick={props.onClose} aria-label="Close gallery">
          <IconClose/>
        </button>
      </div>

      <div className="fsg-stage"
           onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="fsg-track" style={trackStyle}>
          {photos.map(function(p, i) {
            return (
              <div className="fsg-slide" key={i}>
                <img src={p} alt="" loading="lazy" decoding="async" onError={handleErr}/>
              </div>
            );
          })}
        </div>
      </div>

      {photos.length > 1 && (
        <div className="fsg-dots">
          {photos.map(function(_, i) {
            return <button key={i} className={"fsg-dot " + (i === idx ? "on" : "")}
                           onClick={function() { setIdx(i); }} aria-label={"Photo " + (i + 1)}/>;
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   DETAIL PAGE
───────────────────────────────────────── */
function DetailPage(props) {
  var l = props.listing;
  var photos = l.photos && l.photos.length > 0 ? l.photos : (l.imageUrl ? [l.imageUrl] : []);

  var amen = l.amenities || [];
  var hasWifi = amen.indexOf("wifi") !== -1;

  /* ── Full-screen gallery state ── */
  var [galleryOpen, setGalleryOpen] = useState(false);
  var [galleryStart, setGalleryStart] = useState(0);
  function openGallery(i) { setGalleryStart(i || 0); setGalleryOpen(true); }

  /* ── BOOKING WIDGET state ── */
  var [bookingOpen, setBookingOpen] = useState(false);
  var [guestName, setGuestName] = useState("");
  var [checkIn, setCheckIn] = useState("");
  var [checkOut, setCheckOut] = useState("");
  var [guests, setGuests] = useState(1);

  var price = Number(l.pricePerNight) || 0;
  var maxGuests = Number(l.guests) || 12;
  var propName = (l.title && l.title.trim()) ? l.title.trim() : autoTitle(l.propertyType, l.location);

  /* Today in YYYY-MM-DD (local) — used as min date to block past dates */
  var todayStr = (function() {
    var d = new Date();
    var m = String(d.getMonth() + 1).padStart(2, "0");
    var day = String(d.getDate()).padStart(2, "0");
    return d.getFullYear() + "-" + m + "-" + day;
  })();

  /* Nights between two YYYY-MM-DD dates */
  function nightsBetween(ci, co) {
    if (!ci || !co) return 0;
    var d1 = new Date(ci + "T00:00:00");
    var d2 = new Date(co + "T00:00:00");
    var diff = Math.round((d2 - d1) / 86400000);
    return diff > 0 ? diff : 0;
  }
  var nights = nightsBetween(checkIn, checkOut);
  var totalPrice = nights * price;
  var validBooking = checkIn && checkOut && nights > 0 && guests >= 1;

  /* Pretty date: "Mon, 15 Jun 2026" */
  function prettyDate(s) {
    if (!s) return "";
    var d = new Date(s + "T00:00:00");
    if (isNaN(d.getTime())) return s;
    var days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return days[d.getDay()] + ", " + d.getDate() + " " + months[d.getMonth()] + " " + d.getFullYear();
  }

  /* When check-in changes, clear check-out if it's now invalid */
  function onCheckInChange(v) {
    setCheckIn(v);
    if (checkOut && v && new Date(checkOut) <= new Date(v)) setCheckOut("");
  }

  /* Rich WhatsApp message with all booking details */
  function buildBookingLink() {
    var lines = [
      "Hello KUNDA,",
      "",
      "I would like to book:",
      "",
      "Property: " + propName,
    ];
    if (guestName.trim()) lines.push("Guest Name: " + guestName.trim());
    if (l.code) lines.push("Property Code: " + l.code);
    lines.push("Check-in: " + prettyDate(checkIn));
    lines.push("Check-out: " + prettyDate(checkOut));
    lines.push("Guests: " + guests);
    lines.push("Total Nights: " + nights);
    lines.push("Estimated Total: GMD " + totalPrice.toLocaleString());
    lines.push("");
    lines.push("Please confirm availability.");
    lines.push("");
    lines.push("Thank you.");
    return "https://wa.me/" + KUNDA_WA + "?text=" + encodeURIComponent(lines.join("\n"));
  }


  /* Read more state for the About section */
  var [expanded, setExpanded] = useState(false);
  var description = l.description || "A beautiful short-stay home in The Gambia.";
  var isLongDesc = description.length > 220;

  /* Native share (with copy-link fallback) */
  function handleShare() {
    var shareUrl = window.location.href;
    var shareTitle = l.title || "KUNDA listing";
    var shareText = "Check out " + shareTitle + " on KUNDA";
    if (navigator.share) {
      navigator.share({ title: shareTitle, text: shareText, url: shareUrl }).catch(function() {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
    }
  }

  return (
    <div className="detv2">
      {/* HERO — full-bleed image with rounded bottom corners, floating glass controls */}
      <div className="detv2-hero">
        <PhotoCarousel photos={photos} large eagerFirst onExpand={openGallery}/>

        {/* Floating glass back button — top left */}
        <button className="detv2-fab detv2-fab-back"
                onClick={props.onBack}
                aria-label="Back">
          <IconArrowLeft/>
        </button>

        {/* Floating glass action buttons — top right */}
        <div className="detv2-fab-group">
          <button className="detv2-fab"
                  onClick={handleShare}
                  aria-label="Share">
            <IconShare/>
          </button>
          <button className="detv2-fab"
                  onClick={function() { props.onSave(l.id); }}
                  aria-label={props.saved ? "Remove from wishlist" : "Save to wishlist"}>
            <IconHeart filled={props.saved}/>
          </button>
        </div>
      </div>

      {/* FLOATING CONTENT CARD — overlaps hero from bottom for layered depth */}
      <div className="detv2-card">

        {/* TITLE — large centered serif, max 2 lines */}
        <h1 className="detv2-title">{l.title || "A stay in The Gambia"}</h1>

        {/* LOCATION */}
        <div className="detv2-loc">
          <IconPin/>
          <span>{l.location ? l.location + ", The Gambia" : "The Gambia"}</span>
        </div>

        {/* PRICE — priority info, visible without scrolling */}
        <div className="detv2-price-lead">
          <strong>GMD {Number(l.pricePerNight || 0).toLocaleString()}</strong>
          <span> / night</span>
        </div>

        {/* PROPERTY CODE + VERIFIED BADGE (no host info shown publicly) */}
        {l.code && (
          <div className="detv2-prop">
            <span>Property Code · {l.code}</span>
            <span className="detv2-verified" aria-label="Verified property">
              <IconVerified/>
            </span>
          </div>
        )}

        {/* QUICK INFO ROW */}
        <div className="detv2-quick">
          <div className="detv2-quick-item">
            <IconUsers/>
            <span>{(l.guests || 2)} {(l.guests || 2) === 1 ? "Guest" : "Guests"}</span>
          </div>
          <div className="detv2-quick-sep"/>
          <div className="detv2-quick-item">
            <IconBed/>
            <span>{(l.beds || 1)} {(l.beds || 1) === 1 ? "Bedroom" : "Bedrooms"}</span>
          </div>
          <div className="detv2-quick-sep"/>
          <div className="detv2-quick-item">
            <IconBath/>
            <span>{(l.baths || 1)} {(l.baths || 1) === 1 ? "Bathroom" : "Bathrooms"}</span>
          </div>
          {hasWifi && (
            <>
              <div className="detv2-quick-sep"/>
              <div className="detv2-quick-item">
                <IconWifi/>
                <span>WiFi</span>
              </div>
            </>
          )}
        </div>

        <div className="detv2-divider"/>

        {/* PROPERTY HIGHLIGHTS — clear, visual trust/value cues */}
        {(function() {
          var hl = [];
          if (amen.indexOf("wifi") !== -1) hl.push({ icon: "📶", label: "WiFi Available" });
          if (amen.indexOf("beach_access") !== -1) hl.push({ icon: "🏝", label: "Beach Access" });
          if (amen.indexOf("security") !== -1) hl.push({ icon: "🛡️", label: "Secure Area" });
          if (amen.indexOf("parking") !== -1) hl.push({ icon: "🅿️", label: "Parking Available" });
          if (amen.indexOf("ac") !== -1) hl.push({ icon: "❄️", label: "Air Conditioning" });
          if (amen.indexOf("kitchen") !== -1) hl.push({ icon: "🍳", label: "Full Kitchen" });
          if (amen.indexOf("generator") !== -1) hl.push({ icon: "🔌", label: "Backup Power" });
          if ((Number(l.guests) || 0) >= 4) hl.push({ icon: "👨‍👩‍👧", label: "Family Friendly" });
          if (hl.length === 0) return null;
          return (
            <>
              <h2 className="detv2-h">Highlights</h2>
              <div className="detv2-highlights">
                {hl.slice(0, 6).map(function(h, i) {
                  return (
                    <div key={i} className="detv2-highlight">
                      <span className="detv2-highlight-icon">{h.icon}</span>
                      <span className="detv2-highlight-label">{h.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="detv2-divider"/>
            </>
          );
        })()}

        {/* ABOUT */}
        <h2 className="detv2-h">About this home</h2>
        <p className={"detv2-p" + (expanded || !isLongDesc ? " is-expanded" : "")}>
          {description}
        </p>
        {isLongDesc && (
          <button className="detv2-readmore"
                  onClick={function() { setExpanded(!expanded); }}>
            {expanded ? "Read less" : "Read more"}
          </button>
        )}

        {amen.length > 0 && (
          <>
            <div className="detv2-divider"/>

            {/* AMENITIES — 2-column grid */}
            <h2 className="detv2-h">What this place offers</h2>
            <div className="detv2-amen-grid">
              {amen.map(function(id) {
                var a = AMENITIES.find(function(x) { return x.id === id; });
                if (!a) return null;
                return (
                  <div key={id} className="detv2-amen">
                    <span className="detv2-amen-icon">{a.icon}</span>
                    <span className="detv2-amen-label">{a.label}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="detv2-divider"/>

        {/* HOUSE RULES */}
        <h2 className="detv2-h">House rules</h2>
        <ul className="detv2-list">
          <li>Check-in after 2:00 PM</li>
          <li>Check-out before 11:00 AM</li>
          <li>No smoking inside</li>
          <li>No parties or events</li>
          <li>Pets not allowed</li>
        </ul>

        <div className="detv2-divider"/>

        {/* SAFETY & PROPERTY NOTES */}
        <h2 className="detv2-h">Safety &amp; property notes</h2>
        <ul className="detv2-list">
          <li>Backup water available</li>
          <li>Quiet, secure neighborhood</li>
          <li>24/7 KUNDA support during your stay</li>
        </ul>

        {/* TRUST CARD — sits at end of content, sticky booking bar floats above */}
        <div className="detv2-trust">
          <span className="detv2-trust-icon" aria-hidden="true">🛡️</span>
          <div className="detv2-trust-text">
            <strong>KUNDA Protection</strong>
            <span className="detv2-trust-line">Every listing is reviewed before going live.</span>
            <span className="detv2-trust-line">Bookings are managed through KUNDA.</span>
            <span className="detv2-trust-line">Support is available throughout your stay.</span>
          </div>
        </div>

        {/* Spacer so sticky booking bar never overlaps content */}
        <div className="detv2-spacer"/>
      </div>

      {/* STICKY BOOKING BAR — replaces bottom nav on detail page (nav hides when detail is open) */}
      <div className="detv2-booking">
        <div className="detv2-booking-left">
          <div className="detv2-price">
            <strong>GMD {Number(l.pricePerNight || 0).toLocaleString()}</strong>
            <span> / night</span>
          </div>
          <div className="detv2-secure">
            <IconVerified/> Secure booking via KUNDA
          </div>
        </div>
        <button className="detv2-book-btn" onClick={function() { setBookingOpen(true); }}>
          <WaIcon/> Book on WhatsApp
        </button>
      </div>

      {/* ── FULL-SCREEN GALLERY ── */}
      {galleryOpen && (
        <FullScreenGallery photos={photos} startIndex={galleryStart}
                           onClose={function() { setGalleryOpen(false); }}/>
      )}

      {/* ── BOOKING WIDGET — bottom sheet ── */}
      {bookingOpen && (
        <div className="bsheet-overlay" onClick={function() { setBookingOpen(false); }}>
          <div className="bsheet" onClick={function(e) { e.stopPropagation(); }}>
            <div className="bsheet-handle"/>
            <button className="bsheet-close" onClick={function() { setBookingOpen(false); }} aria-label="Close">
              <IconClose/>
            </button>

            <h2 className="bsheet-title">Request to book</h2>
            <div className="bsheet-prop">{propName}</div>

            {/* Your name — so the host knows who's coming */}
            <label className="bsheet-field bsheet-name">
              <span className="bsheet-field-label">Your name</span>
              <input type="text" className="bsheet-text" placeholder="e.g. Awa Touray"
                     value={guestName} maxLength={50}
                     onChange={function(e) { setGuestName(e.target.value); }}/>
            </label>

            {/* Dates */}
            <div className="bsheet-dates">
              <label className="bsheet-field">
                <span className="bsheet-field-label">Check-in</span>
                <input type="date" className="bsheet-date" value={checkIn} min={todayStr}
                       onChange={function(e) { onCheckInChange(e.target.value); }}/>
              </label>
              <label className="bsheet-field">
                <span className="bsheet-field-label">Check-out</span>
                <input type="date" className="bsheet-date" value={checkOut}
                       min={checkIn || todayStr} disabled={!checkIn}
                       onChange={function(e) { setCheckOut(e.target.value); }}/>
              </label>
            </div>

            {/* Guests */}
            <div className="bsheet-guests">
              <div className="bsheet-guests-label">
                <span className="bsheet-field-label">Guests</span>
                <span className="bsheet-guests-max">Up to {maxGuests}</span>
              </div>
              <Stepper value={guests} setValue={setGuests} min={1} max={maxGuests} label={guests === 1 ? "guest" : "guests"}/>
            </div>

            {/* Live price breakdown */}
            {validBooking ? (
              <div className="bsheet-calc">
                <div className="bsheet-calc-row">
                  <span>GMD {price.toLocaleString()} × {nights} {nights === 1 ? "night" : "nights"}</span>
                  <span>GMD {totalPrice.toLocaleString()}</span>
                </div>
                <div className="bsheet-calc-total">
                  <span>Estimated total</span>
                  <strong>GMD {totalPrice.toLocaleString()}</strong>
                </div>
              </div>
            ) : (
              <div className="bsheet-hint">
                {!checkIn ? "Select your check-in date to see the total."
                  : !checkOut ? "Now choose your check-out date."
                  : "Check-out must be after check-in."}
              </div>
            )}

            {/* Trust checklist */}
            <div className="bsheet-trust">
              <div className="bsheet-trust-item"><IconVerified/> Verified property</div>
              <div className="bsheet-trust-item"><IconVerified/> Secure booking process</div>
              <div className="bsheet-trust-item"><IconVerified/> KUNDA support available</div>
              <div className="bsheet-trust-item"><IconVerified/> Fast host response</div>
            </div>

            {/* Continue */}
            {validBooking ? (
              <a href={buildBookingLink()} target="_blank" rel="noreferrer"
                 className="bsheet-continue" onClick={function() { setBookingOpen(false); }}>
                <WaIcon/> Continue on WhatsApp
              </a>
            ) : (
              <button className="bsheet-continue is-off" disabled>
                <WaIcon/> Continue on WhatsApp
              </button>
            )}
            <div className="bsheet-foot">Booking managed through KUNDA · Secure reservation process</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   IMAGE COMPRESSION
   Resizes + re-encodes a photo before upload. A raw phone photo can be
   4–8 MB; this brings it down to ~200–500 KB — roughly 10× smaller,
   which makes listing submission ~10× faster. Always resolves (never
   rejects): on any failure it falls back to the original file so a
   submission can never break because of compression.
───────────────────────────────────────── */
/* compressImage now lives in ./imageUtils (shared with longterm.js) */

/* ─────────────────────────────────────────
   HOST WIZARD V2 — 11-step Airbnb-style flow
   Pending review on submit, auto-return to dashboard,
   no public host info exposure, 5% KUNDA fee shown to host
───────────────────────────────────────── */
function HostWizard(props) {
  var TOTAL_STEPS = 11;
  var LAST_STEP = TOTAL_STEPS - 1;
  var [step, setStep] = useState(0);
  var [submitting, setSubmitting] = useState(false);
  var [submitted, setSubmitted] = useState(false);
  var [submitPhase, setSubmitPhase] = useState("");
  var [error, setError] = useState("");

  /* Form state */
  var [propType, setPropType] = useState("");
  var [area, setArea] = useState("");
  var [customLocMode, setCustomLocMode] = useState(false);
  var [customLocation, setCustomLocation] = useState("");
  var [title, setTitle] = useState("");
  var [photoFiles, setPhotoFiles] = useState([]);
  var [photoPreviews, setPhotoPreviews] = useState([]);
  var [guests, setGuests] = useState(2);
  var [bedrooms, setBedrooms] = useState(1);
  var [beds, setBeds] = useState(1);
  var [baths, setBaths] = useState(1);
  var [hasWifi, setHasWifi] = useState(true);
  var [amen, setAmen] = useState([]);
  var [description, setDescription] = useState("");
  var [checkIn, setCheckIn] = useState("14:00");
  var [checkOut, setCheckOut] = useState("11:00");
  var [noSmoking, setNoSmoking] = useState(true);
  var [noParties, setNoParties] = useState(true);
  var [petsAllowed, setPetsAllowed] = useState(false);
  var [childrenAllowed, setChildrenAllowed] = useState(true);
  var [additionalRules, setAdditionalRules] = useState("");
  var [price, setPrice] = useState("");
  var initialName = (props.user && props.user.name) ? props.user.name : "";
  var [name, setName] = useState(initialName);
  var [phone, setPhone] = useState("");

  /* Computed */
  var locationLabel = customLocMode ? customLocation : area;

  /* Build a professional listing description from the host's inputs.
     Hosts can edit the result before publishing. */
  function generateDescription() {
    var typeObj = PROPERTY_TYPES.find(function(t) { return t.id === propType; });
    var typeLabel = typeObj ? typeObj.label.toLowerCase() : "place";
    var loc = (locationLabel && locationLabel.trim()) ? locationLabel.trim() : "The Gambia";

    /* Opening line */
    var openers = [
      "Enjoy a comfortable stay in " + loc + ".",
      "Relax and unwind in this welcoming " + typeLabel + " in " + loc + ".",
      "Make yourself at home in this lovely " + typeLabel + " in " + loc + "."
    ];
    var opener = openers[Math.floor(Math.random() * openers.length)];

    /* Space summary */
    var spaceParts = [];
    if (bedrooms > 0) spaceParts.push(bedrooms + (bedrooms === 1 ? " bedroom" : " bedrooms"));
    if (baths > 0) spaceParts.push(baths + (baths === 1 ? " bathroom" : " bathrooms"));
    var spaceLine = "";
    if (spaceParts.length) {
      spaceLine = "This " + typeLabel + " offers " + spaceParts.join(" and ") +
                  ", comfortably hosting up to " + guests + (guests === 1 ? " guest" : " guests") + ".";
    }

    /* Amenities highlight (pick the most appealing, readable ones) */
    var allAmen = amen.slice();
    if (hasWifi && allAmen.indexOf("wifi") < 0) allAmen.unshift("wifi");
    var niceLabels = {
      wifi: "reliable WiFi", ac: "air conditioning", kitchen: "a fully equipped kitchen",
      hot_water: "hot water", water: "running water", parking: "private parking",
      security: "24/7 security", generator: "backup power", tv: "a smart TV",
      washer: "a washing machine", balcony: "a private balcony", beach_access: "direct beach access"
    };
    var picked = [];
    allAmen.forEach(function(id) { if (niceLabels[id]) picked.push(niceLabels[id]); });
    var amenLine = "";
    if (picked.length) {
      var top = picked.slice(0, 4);
      var last = top.pop();
      amenLine = "Guests will enjoy " + (top.length ? top.join(", ") + ", and " + last : last) + ".";
    }

    /* Closing line */
    var closer = "You'll have easy access to beaches, restaurants, and local attractions — everything you need for a memorable stay in " + loc + ".";

    return [opener, spaceLine, amenLine, closer].filter(Boolean).join(" ");
  }

  var priceNum = Number(price) || 0;
  var feeAmount = Math.round(priceNum * KUNDA_FEE_RATE);
  var hostEarn = priceNum - feeAmount;

  /* Auto-generated title suggestions */
  function getTitleSuggestions() {
    if (!propType || !locationLabel) return [];
    var t = PROPERTY_TYPES.find(function(p) { return p.id === propType; });
    var ptLabel = t ? t.label : "Stay";
    var loc = (locationLabel || "").replace(/,\s*(The\s+)?Gambia\s*$/i, "").trim() || "The Gambia";
    if (propType === "beach_home") {
      return ["Beach Home in " + loc, "Beach stay near " + loc, ptLabel + " near " + loc];
    }
    if (propType === "studio") {
      return ["Studio in " + loc, "Cozy Studio in " + loc, "Modern Studio in " + loc];
    }
    if (propType === "room") {
      return ["Room in " + loc, "Private Room in " + loc, "Cozy Room in " + loc];
    }
    return [ptLabel + " in " + loc, "Modern " + ptLabel + " in " + loc, "Spacious " + ptLabel + " in " + loc];
  }

  /* Validation per step */
  function canContinue() {
    if (step === 0) return !!propType;
    if (step === 1) return customLocMode ? !!customLocation.trim() : !!area;
    if (step === 2) return title.trim().length > 0;
    if (step === 3) return photoFiles.length > 0;
    if (step === 8) return priceNum > 0;
    if (step === 9) return name.trim().length > 0 && phone.trim().length >= 7;
    return true;
  }

  /* Navigation */
  function goNext() {
    if (!canContinue() || submitting) return;
    if (step === LAST_STEP) { doSubmit(); return; }
    setStep(step + 1);
    /* Instant scroll prevents any smooth-scroll lag on step change */
    if (window.scrollTo) window.scrollTo(0, 0);
  }
  function goBack() {
    if (step === 0 || submitting) return;
    setStep(step - 1);
    if (window.scrollTo) window.scrollTo(0, 0);
  }

  /* Photo handling */
  function onPhotoSelect(e) {
    var files = Array.from(e.target.files || []);
    if (!files.length) return;
    var newFiles = photoFiles.concat(files);
    var newPreviews = photoPreviews.concat(files.map(function(f) { return URL.createObjectURL(f); }));
    setPhotoFiles(newFiles);
    setPhotoPreviews(newPreviews);
    e.target.value = "";
  }
  function removePhoto(idx) {
    /* Free the browser memory used by the photo preview blob */
    var removedUrl = photoPreviews[idx];
    if (removedUrl && removedUrl.indexOf("blob:") === 0) {
      try { URL.revokeObjectURL(removedUrl); } catch (e) {}
    }
    var f = photoFiles.slice();
    var p = photoPreviews.slice();
    f.splice(idx, 1);
    p.splice(idx, 1);
    setPhotoFiles(f);
    setPhotoPreviews(p);
  }
  function makeCover(idx) {
    if (idx === 0) return;
    var f = photoFiles.slice();
    var p = photoPreviews.slice();
    var movedF = f.splice(idx, 1)[0];
    var movedP = p.splice(idx, 1)[0];
    f.unshift(movedF);
    p.unshift(movedP);
    setPhotoFiles(f);
    setPhotoPreviews(p);
  }

  function toggleAmen(id) {
    if (amen.indexOf(id) >= 0) {
      setAmen(amen.filter(function(x) { return x !== id; }));
    } else {
      setAmen(amen.concat([id]));
    }
  }

  /* Generate property code based on area */
  function generatePropCode() {
    var prefix = "GM";
    if (!customLocMode) {
      var a = AREAS.find(function(x) { return x.name === area; });
      if (a) prefix = a.code;
    }
    var num = Math.floor(1000 + Math.random() * 9000);
    return prefix + "-" + num;
  }

  /* Submit */
  async function doSubmit() {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      /* PHASE 1 — compress every photo in parallel, at TWO sizes:
         a tiny thumbnail (~520px) for cards, and a full (~1600px) for the
         property page + gallery. Cards loading 520px WebP instead of 1600px
         is the single biggest speed win on slow mobile data. */
      setSubmitPhase("Optimizing your photos…");
      var fullBlobs = await Promise.all(
        photoFiles.map(function(f) { return compressImage(f, 1600, 0.82); })
      );
      var thumbBlobs = await Promise.all(
        photoFiles.map(function(f) { return compressImage(f, 520, 0.74); })
      );

      /* PHASE 2 — upload all photos in parallel (full + thumb together). */
      setSubmitPhase("Uploading your photos…");
      var stamp = Date.now();
      function extFor(blob) {
        if (blob && blob.type === "image/webp") return ".webp";
        if (blob && blob.type === "image/jpeg") return ".jpg";
        return "";
      }
      function uploadBlob(blob, name) {
        var ref = sRef(storage, name);
        /* Cache for a year — filenames are unique per upload (timestamped), so
           "immutable" is safe. Returning visitors load images straight from the
           browser cache instead of re-downloading on mobile data. */
        var meta = { cacheControl: "public, max-age=31536000, immutable" };
        return uploadBytes(ref, blob, meta).then(function(snap) { return getDownloadURL(snap.ref); });
      }
      var photoUrls = await Promise.all(
        fullBlobs.map(function(blob, i) {
          return uploadBlob(blob, "listings/" + stamp + "_" + i + extFor(blob));
        })
      );
      var thumbUrls = await Promise.all(
        thumbBlobs.map(function(blob, i) {
          return uploadBlob(blob, "listings/" + stamp + "_" + i + "_thumb" + extFor(blob));
        })
      );

      /* PHASE 3 — write the listing document */
      setSubmitPhase("Almost ready…");
      var finalAmen = amen.slice();
      if (hasWifi && finalAmen.indexOf("wifi") < 0) finalAmen.push("wifi");
      if (!hasWifi) finalAmen = finalAmen.filter(function(x) { return x !== "wifi"; });

      var newCode = generatePropCode();

      await addDoc(collection(db, LISTINGS_COL), {
        code:          newCode,
        title:         title.trim(),
        propertyType:  propType,
        location:      locationLabel.trim(),
        description:   description.trim(),
        pricePerNight: priceNum,
        currency:      "GMD",
        beds:          Number(beds),
        bedrooms:      Number(bedrooms),
        baths:         Number(baths),
        guests:        Number(guests),
        amenities:     finalAmen,
        photos:        photoUrls,
        photoThumbs:   thumbUrls,
        imageUrl:      photoUrls[0] || "",
        houseRules: {
          checkIn:         checkIn,
          checkOut:        checkOut,
          noSmoking:       noSmoking,
          noParties:       noParties,
          petsAllowed:     petsAllowed,
          childrenAllowed: childrenAllowed,
          additionalRules: additionalRules.trim()
        },
        hostId:        (props.user && props.user.uid) ? props.user.uid : null,
        hostName:      name.trim(),
        hostEmail:     (props.user && props.user.email) ? props.user.email : "",
        hostPhone:     phone.replace(/\D/g, ""),
        status:        "pending",
        hidden:        false,
        createdAt:     serverTimestamp(),
        updatedAt:     serverTimestamp(),
      });

      setSubmitting(false);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (e) {
      console.error(e);
      setError("Something went wrong. Check your connection and try again.");
      setSubmitting(false);
      setSubmitPhase("");
    }
  }

  var stepTitles = [
    { h: "What are you listing?",       sub: "Choose the type of property" },
    { h: "Where is your place located?", sub: "Choose an area or enter your own" },
    { h: "Create a great title",         sub: "Make it inviting and clear" },
    { h: "Add photos of your place",     sub: "Beautiful photos get more bookings" },
    { h: "Tell us about your place",     sub: "Just the basics" },
    { h: "What amenities do you offer?", sub: "Select all that apply" },
    { h: "Describe your place",          sub: "Help guests imagine their stay" },
    { h: "Set your house rules",         sub: "Make expectations clear up front" },
    { h: "Set your price",               sub: "You can always change this later" },
    { h: "Tell us about you",            sub: "Only visible to KUNDA — never shown publicly" },
    { h: "Review your listing",          sub: "Check everything before submitting" }
  ];
  var st = stepTitles[step];
  var progressPct = ((step + 1) / TOTAL_STEPS) * 100;

  /* ── SUCCESS SCREEN — polished confirmation after submission ── */
  if (submitted) {
    return (
      <div className="wiz">
        <div className="success-screen">
          <div className="success-check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h1 className="success-title">Listing submitted</h1>
          <p className="success-sub">
            Your property is now being reviewed by KUNDA. We'll notify you once it's approved and live — usually within 24 hours.
          </p>
          <div className="success-steps">
            <div className="success-step"><span className="success-step-ic">✓</span> Photos and details received</div>
            <div className="success-step"><span className="success-step-ic success-step-now">●</span> Under review by KUNDA</div>
            <div className="success-step success-step-next"><span className="success-step-ic">○</span> Goes live once approved</div>
          </div>
          <button className="btn-primary success-btn" onClick={function() { if (props.onDone) props.onDone(); }}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wiz">
      {/* PREMIUM SUBMISSION OVERLAY — shown while compressing + uploading.
          Replaces the old frozen "Submitting..." button state. */}
      {submitting && (
        <div className="wiz-loading">
          <div className="wiz-loading-box">
            <div className="wiz-loading-ring"/>
            <div className="wiz-loading-title">
              {submitPhase || "Submitting your property…"}
            </div>
            <div className="wiz-loading-sub">Hang tight — this won't take long</div>
          </div>
        </div>
      )}

      <div className="wiz-top">
        <button className="wiz-close" onClick={props.onCancel} aria-label="Cancel" disabled={submitting}>
          <IconClose/>
        </button>
        <div className="wiz-progress">
          <div className="wiz-progress-fill" style={{ width: progressPct + "%" }}/>
        </div>
        <div className="wiz-step">{step + 1} / {TOTAL_STEPS}</div>
      </div>

      <div className="wiz-content">
        <div className="wiz-screen" key={step}>
          <h1 className="wiz-h1">{st.h}</h1>
          <p className="wiz-sub">{st.sub}</p>

          {/* === STEP 0: PROPERTY TYPE === */}
          {step === 0 && (
            <div className="wiz-grid wiz-grid-types">
              {PROPERTY_TYPES.map(function(t) {
                var selected = propType === t.id;
                return (
                  <button key={t.id}
                          className={"wiz-card " + (selected ? "is-selected" : "")}
                          onClick={function() { setPropType(t.id); }}>
                    <div className="wiz-card-icon">{t.icon}</div>
                    <div className="wiz-card-label">{t.label}</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* === STEP 1: LOCATION === */}
          {step === 1 && (
            <>
              {!customLocMode && (
                <div className="wiz-grid wiz-grid-locs">
                  {AREAS.map(function(a) {
                    var selected = area === a.name;
                    return (
                      <button key={a.name}
                              className={"wiz-loc-btn " + (selected ? "is-selected" : "")}
                              onClick={function() { setArea(a.name); }}>
                        {a.name}
                      </button>
                    );
                  })}
                </div>
              )}

              {customLocMode && (
                <input className="wiz-input"
                       type="text"
                       placeholder="e.g. Yundum, Brikama, Janjanbureh..."
                       value={customLocation}
                       autoFocus
                       onChange={function(e) { setCustomLocation(e.target.value); }}/>
              )}

              <div className="wiz-cant-find">
                {!customLocMode ? (
                  <>
                    <div className="wiz-cant-find-q">Can't find your area?</div>
                    <button className="loc-custom-btn"
                            onClick={function() { setCustomLocMode(true); setArea(""); }}>
                      Enter location manually
                    </button>
                  </>
                ) : (
                  <button className="loc-custom-btn"
                          onClick={function() { setCustomLocMode(false); setCustomLocation(""); }}>
                    Choose from list instead
                  </button>
                )}
              </div>
            </>
          )}

          {/* === STEP 2: TITLE === */}
          {step === 2 && (
            <>
              {getTitleSuggestions().length > 0 && (
                <div className="wiz-suggest-row">
                  {getTitleSuggestions().map(function(s, i) {
                    return (
                      <button key={i}
                              className="wiz-suggest-chip"
                              onClick={function() { setTitle(s); }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              )}
              <input className="wiz-input"
                     type="text"
                     placeholder="Or type your own..."
                     value={title}
                     maxLength={80}
                     onChange={function(e) { setTitle(e.target.value); }}/>
              <div className="wiz-counter">{title.length} / 80</div>
              <p className="wiz-tip">💡 Best titles describe what makes your place special. Max 2 lines on cards.</p>
            </>
          )}

          {/* === STEP 3: PHOTOS === */}
          {step === 3 && (
            <>
              <div className="wiz-photo-grid">
                {photoPreviews.map(function(src, i) {
                  return (
                    <div key={i} className="wiz-photo-cell">
                      <img src={src} alt={"photo " + (i + 1)}/>
                      {i === 0 && (
                        <div className="wiz-photo-cover">
                          <span>★</span> Cover
                        </div>
                      )}
                      <button className="wiz-photo-remove"
                              onClick={function() { removePhoto(i); }}
                              aria-label="Remove photo">
                        ×
                      </button>
                      {i !== 0 && (
                        <button className="wiz-photo-make-cover"
                                onClick={function() { makeCover(i); }}>
                          Make cover
                        </button>
                      )}
                    </div>
                  );
                })}

                <label className="wiz-photo-add">
                  <span className="wiz-photo-add-plus">+</span>
                  <span className="wiz-photo-add-label">Add photos</span>
                  <input type="file"
                         accept="image/*"
                         multiple
                         onChange={onPhotoSelect}/>
                </label>
              </div>

              {/* Photo quality recommendations */}
              <div className="wiz-photo-rec">
                <div className="wiz-photo-rec-head">
                  <span>{photoPreviews.length} added</span>
                  <span className={photoPreviews.length >= 5 ? "wiz-photo-rec-ok" : ""}>
                    {photoPreviews.length >= 5 ? "✓ Great — 5+ photos" : "Aim for at least 5"}
                  </span>
                </div>
                <div className="wiz-photo-rec-bar">
                  <div className="wiz-photo-rec-fill"
                       style={{ width: Math.min(100, (photoPreviews.length / 5) * 100) + "%" }}/>
                </div>
                <p className="wiz-photo-rec-title">Listings with these shots get more bookings:</p>
                <div className="wiz-photo-rec-list">
                  <span>🛋 Living room</span>
                  <span>🛏 Bedroom</span>
                  <span>🚿 Bathroom</span>
                  <span>🍳 Kitchen</span>
                  <span>🏡 Exterior</span>
                </div>
              </div>
            </>
          )}

          {/* === STEP 4: QUICK DETAILS === */}
          {step === 4 && (
            <>
              <Stepper value={guests}   setValue={setGuests}   min={1} max={16} label="Guests"/>
              <Stepper value={bedrooms} setValue={setBedrooms} min={0} max={10} label="Bedrooms"/>
              <Stepper value={beds}     setValue={setBeds}     min={1} max={16} label="Beds"/>
              <Stepper value={baths}    setValue={setBaths}    min={1} max={10} label="Bathrooms"/>

              <div className="wiz-toggle">
                <div>
                  <div className="wiz-toggle-label">WiFi available</div>
                  <div className="wiz-toggle-sub">Most guests look for this</div>
                </div>
                <Switch on={hasWifi} onChange={setHasWifi}/>
              </div>
            </>
          )}

          {/* === STEP 5: AMENITIES === */}
          {step === 5 && (
            <div className="wiz-grid wiz-grid-amens">
              {AMENITIES.map(function(a) {
                var selected = amen.indexOf(a.id) >= 0;
                return (
                  <button key={a.id}
                          className={"wiz-card wiz-amen-card " + (selected ? "is-selected" : "")}
                          onClick={function() { toggleAmen(a.id); }}>
                    <div className="wiz-card-icon">{a.icon}</div>
                    <div className="wiz-card-label">{a.label}</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* === STEP 6: ABOUT === */}
          {step === 6 && (
            <>
              <button className="wiz-gen-btn" type="button"
                      onClick={function() { setDescription(generateDescription()); }}>
                ✨ {description.trim() ? "Regenerate description" : "Generate a description for me"}
              </button>
              <textarea className="wiz-textarea"
                        placeholder="Describe your place — what makes it special, the location, what guests will love..."
                        value={description}
                        maxLength={1000}
                        onChange={function(e) { setDescription(e.target.value); }}/>
              <div className="wiz-counter">{description.length} / 1000</div>
              <div className="wiz-tips">
                <p>✨ Tap “Generate” for a professional starting point, then edit freely</p>
                <p>🌴 Highlight nearby beaches, markets, or attractions</p>
                <p>🤝 Be honest and helpful — guests appreciate clarity</p>
              </div>
            </>
          )}

          {/* === STEP 7: HOUSE RULES === */}
          {step === 7 && (
            <>
              <div className="wiz-time-row">
                <div className="wiz-time-cell">
                  <label className="wiz-toggle-label">Check-in time</label>
                  <input className="wiz-input wiz-input-time"
                         type="time"
                         value={checkIn}
                         onChange={function(e) { setCheckIn(e.target.value); }}/>
                </div>
                <div className="wiz-time-cell">
                  <label className="wiz-toggle-label">Check-out time</label>
                  <input className="wiz-input wiz-input-time"
                         type="time"
                         value={checkOut}
                         onChange={function(e) { setCheckOut(e.target.value); }}/>
                </div>
              </div>

              <div className="wiz-toggles-list">
                <div className="wiz-toggle">
                  <div>
                    <div className="wiz-toggle-label">No smoking</div>
                    <div className="wiz-toggle-sub">Indoor smoking is not allowed</div>
                  </div>
                  <Switch on={noSmoking} onChange={setNoSmoking}/>
                </div>
                <div className="wiz-toggle">
                  <div>
                    <div className="wiz-toggle-label">No parties or events</div>
                    <div className="wiz-toggle-sub">Quiet stays only</div>
                  </div>
                  <Switch on={noParties} onChange={setNoParties}/>
                </div>
                <div className="wiz-toggle">
                  <div>
                    <div className="wiz-toggle-label">Pets allowed</div>
                    <div className="wiz-toggle-sub">Guests can bring small pets</div>
                  </div>
                  <Switch on={petsAllowed} onChange={setPetsAllowed}/>
                </div>
                <div className="wiz-toggle">
                  <div>
                    <div className="wiz-toggle-label">Children allowed</div>
                    <div className="wiz-toggle-sub">Family-friendly stay</div>
                  </div>
                  <Switch on={childrenAllowed} onChange={setChildrenAllowed}/>
                </div>
              </div>

              <label className="wiz-toggle-label wiz-extra-rules-label">
                Additional rules (optional)
              </label>
              <textarea className="wiz-textarea wiz-textarea-small"
                        placeholder="e.g. Quiet hours after 10 PM, please remove shoes inside..."
                        value={additionalRules}
                        maxLength={400}
                        onChange={function(e) { setAdditionalRules(e.target.value); }}/>
            </>
          )}

          {/* === STEP 8: PRICE === */}
          {step === 8 && (
            <>
              <div className="wiz-price-input-wrap">
                <span className="wiz-price-currency">GMD</span>
                <input className="wiz-input wiz-price-input"
                       type="number"
                       inputMode="numeric"
                       placeholder="0"
                       value={price}
                       autoFocus
                       onChange={function(e) { setPrice(e.target.value); }}/>
                <span className="wiz-price-suffix">/ night</span>
              </div>

              {priceNum > 0 && (
                <div className="wiz-price-break">
                  <div className="wiz-price-row">
                    <span>Guest pays</span>
                    <strong>GMD {priceNum.toLocaleString()}</strong>
                  </div>
                  <div className="wiz-price-row wiz-price-row-sub">
                    <span>KUNDA fee (5%)</span>
                    <span>− GMD {feeAmount.toLocaleString()}</span>
                  </div>
                  <div className="wiz-price-divider"/>
                  <div className="wiz-price-row wiz-price-row-earn">
                    <span>You earn</span>
                    <strong>GMD {hostEarn.toLocaleString()}</strong>
                  </div>
                </div>
              )}
            </>
          )}

          {/* === STEP 9: TELL US ABOUT YOU === */}
          {step === 9 && (
            <>
              <label className="wiz-toggle-label wiz-field-label">Your name</label>
              <input className="wiz-input"
                     type="text"
                     placeholder="Full name"
                     value={name}
                     onChange={function(e) { setName(e.target.value); }}/>

              <label className="wiz-toggle-label wiz-field-label">Phone number</label>
              <input className="wiz-input"
                     type="tel"
                     inputMode="tel"
                     placeholder="+220 ..."
                     value={phone}
                     onChange={function(e) { setPhone(e.target.value); }}/>

              <div className="wiz-priv-note">
                🔒 This information is <strong>only visible to KUNDA</strong> for verification purposes. Your contact details are never shown publicly on listings.
              </div>
            </>
          )}

          {/* === STEP 10: REVIEW === */}
          {step === 10 && (
            <>
              {photoPreviews[0] && (
                <div className="wiz-review-cover">
                  <img src={photoPreviews[0]} alt=""/>
                </div>
              )}
              <h2 className="wiz-review-title">{title || "Your listing"}</h2>
              <div className="wiz-review-loc">📍 {locationLabel}, The Gambia</div>

              <div className="wiz-review-card">
                <div className="wiz-review-row-r">
                  <span>Property type</span>
                  <strong>{(PROPERTY_TYPES.find(function(p) { return p.id === propType; }) || { label: "—" }).label}</strong>
                </div>
                <div className="wiz-review-row-r">
                  <span>Guests</span>
                  <strong>{guests}</strong>
                </div>
                <div className="wiz-review-row-r">
                  <span>Bedrooms · Beds · Baths</span>
                  <strong>{bedrooms} · {beds} · {baths}</strong>
                </div>
                <div className="wiz-review-row-r">
                  <span>Photos</span>
                  <strong>{photoFiles.length}</strong>
                </div>
                <div className="wiz-review-row-r">
                  <span>Amenities</span>
                  <strong>{amen.length + (hasWifi && amen.indexOf("wifi") < 0 ? 1 : 0)}</strong>
                </div>
                <div className="wiz-review-row-r">
                  <span>Check-in / out</span>
                  <strong>{checkIn} · {checkOut}</strong>
                </div>
                <div className="wiz-review-divider"/>
                <div className="wiz-review-row-r wiz-review-row-price">
                  <span>Price per night</span>
                  <strong>GMD {priceNum.toLocaleString()}</strong>
                </div>
                <div className="wiz-review-row-r wiz-review-row-earn">
                  <span>You earn per night</span>
                  <strong>GMD {hostEarn.toLocaleString()}</strong>
                </div>
              </div>

              <div className="wiz-submit-note">
                🌿 We'll review your listing and get back to you soon. Once approved, your listing will go live on KUNDA.
              </div>
            </>
          )}

          {error && <div className="wiz-err">{error}</div>}
        </div>
      </div>

      <div className="wiz-bottom">
        <div className="wiz-bottom-row">
          {step > 0 ? (
            <button className="wiz-back" onClick={goBack} disabled={submitting}>
              Back
            </button>
          ) : (
            <span/>
          )}

          {step === LAST_STEP ? (
            <button className={"wiz-next " + (submitting ? "off" : "")}
                    onClick={goNext}
                    disabled={submitting}>
              {submitting ? "Submitting..." : "Submit for Review"}
            </button>
          ) : (
            <button className={"wiz-next " + (canContinue() ? "" : "off")}
                    onClick={goNext}
                    disabled={!canContinue() || submitting}>
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* iOS-style toggle switch (reused across wizard) */
function Switch(props) {
  return (
    <button type="button"
            className={"wiz-switch " + (props.on ? "is-on" : "")}
            onClick={function() { props.onChange(!props.on); }}
            aria-checked={props.on}
            role="switch">
      <span className="wiz-switch-thumb"/>
    </button>
  );
}

/* Counter stepper (− value +) */
function Stepper(props) {
  function dec() { if (props.value > props.min) props.setValue(props.value - 1); }
  function inc() { if (props.value < props.max) props.setValue(props.value + 1); }
  return (
    <div className="stepper">
      <button className="step-btn" onClick={dec} disabled={props.value <= props.min}>
        <IconMinus/>
      </button>
      <div className="step-val">
        <div className="step-num">{props.value}</div>
        <div className="step-lbl">{props.label}</div>
      </div>
      <button className="step-btn" onClick={inc} disabled={props.value >= props.max}>
        <IconPlus/>
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────
   AUTH SCREEN — Sign Up / Log In
───────────────────────────────────────── */
function AuthScreen(props) {
  var [mode, setMode]         = useState("login"); // "login" | "signup"
  var [name, setName]         = useState("");
  var [email, setEmail]       = useState("");
  var [password, setPassword] = useState("");
  var [showPw, setShowPw]     = useState(false);
  var [busy, setBusy]         = useState(false);
  var [error, setError]       = useState("");
  var [notice, setNotice]     = useState("");   /* success feedback (P1) */

  function flipMode() {
    setError("");
    setNotice("");
    setMode(mode === "login" ? "signup" : "login");
  }
  function goForgot() {
    setError("");
    setNotice("");
    setMode("forgot");
  }
  function backToLogin() {
    setError("");
    setNotice("");
    setMode("login");
  }

  function readableErr(code) {
    if (!code) return "Something went wrong. Please try again.";
    if (code.indexOf("email-already-in-use") >= 0)  return "This email is already registered. Try logging in instead.";
    if (code.indexOf("invalid-email") >= 0)         return "Please enter a valid email address.";
    if (code.indexOf("weak-password") >= 0)         return "Password should be at least 6 characters.";
    if (code.indexOf("user-not-found") >= 0)        return "No account found with this email.";
    if (code.indexOf("wrong-password") >= 0)        return "Incorrect password. Please try again.";
    if (code.indexOf("invalid-credential") >= 0)    return "Email or password is incorrect.";
    if (code.indexOf("network") >= 0)               return "Network error. Please check your connection.";
    if (code.indexOf("too-many-requests") >= 0)     return "Too many attempts. Please wait a moment and try again.";
    if (code.indexOf("missing-email") >= 0)         return "Please enter your email address.";
    return "Something went wrong. Please try again.";
  }

  /* ── Google Sign-In (P2) ──────────────────────────────────
     One KUNDA account per email address. If this email already has an
     email/password account, Firebase refuses the Google credential
     (account-exists-with-different-credential) — we then guide the person
     to sign in with their password rather than silently creating a
     second, duplicate profile. */
  async function handleGoogle() {
    if (busy) return;
    setError("");
    setNotice("");
    setBusy(true);
    try {
      var provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(auth, provider);
      /* The auth listener in App picks this up, creates the users/{uid}
         profile if absent, and closes this screen. */
    } catch (e) {
      var c = (e && e.code) ? e.code : "";
      console.error("Google sign-in error:", c);

      if (c.indexOf("account-exists-with-different-credential") >= 0) {
        var existingEmail = (e && e.customData && e.customData.email) || "";
        if (existingEmail) setEmail(existingEmail);
        setMode("login");
        setError("You already have a KUNDA account with this email. Log in with your password below — you can connect Google from your profile afterwards.");
      } else if (c.indexOf("popup-blocked") >= 0) {
        /* Some in-app/mobile browsers block popups — fall back to redirect */
        try {
          await signInWithRedirect(auth, new GoogleAuthProvider());
          return;
        } catch (e2) {
          setError("Your browser blocked the Google sign-in window. Please allow popups and try again.");
        }
      } else if (c.indexOf("popup-closed-by-user") >= 0 || c.indexOf("cancelled-popup-request") >= 0) {
        /* The person simply backed out — not an error worth shouting about */
      } else if (c.indexOf("unauthorized-domain") >= 0) {
        setError("Google sign-in isn't enabled for this domain yet. Please try email and password.");
      } else if (c.indexOf("network") >= 0) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Couldn't sign in with Google. Please try again, or use email and password.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleReset() {
    setError("");
    setNotice("");
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    if (email.indexOf("@") < 0) {
      setError("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setNotice("If an account exists for " + email.trim() + ", a password reset link is on its way. Check your inbox — and your spam folder.");
    } catch (e) {
      /* Never confirm or deny that an address is registered: revealing that
         would expose who has a KUNDA account. Only real faults surface. */
      var c = e && e.code ? e.code : "";
      if (c.indexOf("user-not-found") >= 0) {
        setNotice("If an account exists for " + email.trim() + ", a password reset link is on its way. Check your inbox — and your spam folder.");
      } else {
        console.error("Password reset error:", c);
        setError(readableErr(c));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit() {
    setError("");

    /* Validation */
    if (mode === "signup" && name.trim().length < 2) {
      setError("Please enter your name.");
      return;
    }
    if (!email.trim() || email.indexOf("@") < 0) {
      setError("Please enter a valid email.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setBusy(true);
    try {
      if (mode === "signup") {
        var cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        /* Set display name on Firebase user */
        await updateProfile(cred.user, { displayName: name.trim() });
        /* Create user doc in Firestore */
        await setDoc(doc(db, USERS_COL, cred.user.uid), {
          uid:       cred.user.uid,
          name:      name.trim(),
          email:     email.trim().toLowerCase(),
          isHost:    false,
          createdAt: serverTimestamp(),
        });
        /* Verification email — never block signup if the send fails;
           the person can always resend from their profile. */
        try {
          await sendEmailVerification(cred.user);
        } catch (ve) {
          console.error("Verification email failed to send:", ve && ve.code);
        }
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
      /* Auth state listener in App will pick this up and close this screen */
    } catch (e) {
      console.error("Auth error:", e);
      setError(readableErr(e.code || ""));
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e) {
    if (e.key !== "Enter" || busy) return;
    if (mode === "forgot") handleReset();
    else handleSubmit();
  }

  return (
    <div className="auth">
      <div className="auth-top">
        <button className="wiz-close" onClick={props.onCancel}>
          <IconClose/>
        </button>
        <div className="auth-top-title">
          {mode === "forgot" ? "Reset password" : (mode === "login" ? "Log in" : "Sign up")}
        </div>
        <div style={{ width: 36 }}/>
      </div>

      <div className="auth-content">
        <div className="auth-card">
          <div className="auth-logo">
            <img src={kundaLogo} alt="KUNDA" className="auth-logo-img"/>
          </div>
          <h1 className="auth-h1">
            {mode === "forgot" ? "Reset your password"
              : (mode === "login" ? "Welcome back" : "Welcome to KUNDA")}
          </h1>
          <p className="auth-sub">
            {mode === "forgot"
              ? "Enter the email you signed up with and we'll send you a link to set a new password."
              : (mode === "login"
                ? "Log in to access your bookings and saved homes."
                : "Create an account to save homes and track your bookings.")}
          </p>

          {mode === "signup" && (
            <div className="auth-field">
              <label>Full name</label>
              <input
                className="auth-input"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={function(e) { setName(e.target.value); }}
                onKeyDown={onKeyDown}
                autoFocus
              />
            </div>
          )}

          <div className="auth-field">
            <label>Email</label>
            <input
              className="auth-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={function(e) { setEmail(e.target.value); }}
              onKeyDown={onKeyDown}
              autoCapitalize="none"
              autoFocus={mode === "login"}
            />
          </div>

          {mode !== "forgot" && (
          <div className="auth-field">
            <label>Password</label>
            <div className="auth-pw-wrap">
              <input
                className="auth-input"
                type={showPw ? "text" : "password"}
                placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
                value={password}
                onChange={function(e) { setPassword(e.target.value); }}
                onKeyDown={onKeyDown}
              />
              <button
                type="button"
                className="auth-pw-toggle"
                onClick={function() { setShowPw(!showPw); }}
              >
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
            {mode === "login" && (
              <button type="button" className="auth-forgot-link" onClick={goForgot}>
                Forgot password?
              </button>
            )}
          </div>
          )}

          {error && <div className="auth-err">{error}</div>}
          {notice && <div className="auth-notice">{notice}</div>}

          <button
            className="auth-submit"
            onClick={mode === "forgot" ? handleReset : handleSubmit}
            disabled={busy}
          >
            {busy
              ? (mode === "forgot" ? "Sending..." : (mode === "login" ? "Logging in..." : "Creating account..."))
              : (mode === "forgot" ? "Send reset link" : (mode === "login" ? "Log in" : "Create account"))}
          </button>

          {mode !== "forgot" && (
            <>
              <div className="auth-divider"><span>or</span></div>
              <button type="button" className="auth-google" onClick={handleGoogle} disabled={busy}>
                <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
                </svg>
                {busy ? "Please wait…" : "Continue with Google"}
              </button>
            </>
          )}

          {mode === "forgot" ? (
            <div className="auth-switch">
              Remembered it?
              <button className="auth-switch-btn" onClick={backToLogin}>
                Back to log in
              </button>
            </div>
          ) : (
            <div className="auth-switch">
              {mode === "login" ? "New to KUNDA?" : "Already have an account?"}
              <button className="auth-switch-btn" onClick={flipMode}>
                {mode === "login" ? "Sign up" : "Log in"}
              </button>
            </div>
          )}

          <div className="auth-fine">
            By continuing, you agree to KUNDA's terms and acknowledge our privacy practices.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   WISHLIST PAGE
───────────────────────────────────────── */
function WishlistPage(props) {
  var [editing, setEditing] = useState(false);

  var saved = props.saved || [];
  var savedListings = props.listings.filter(function(l) {
    return saved.includes(l.id);
  });

  /* LOGGED OUT */
  if (!props.user) {
    return (
      <div className="prof">
        <div className="wl-header">
          <h1 className="wl-h1">Wishlist</h1>
        </div>
        <div className="auth-prompt">
          <div className="auth-prompt-icon">
            <span style={{ fontSize: 48 }}>❤️</span>
          </div>
          <h2 className="auth-prompt-h2">Save what you love</h2>
          <p className="auth-prompt-sub">
            Log in to save homes and revisit them anytime. Your wishlist will be waiting for you.
          </p>
          <button className="btn-primary auth-prompt-btn" onClick={props.onLogin}>
            Log in or sign up
          </button>
        </div>
      </div>
    );
  }

  /* EMPTY STATE */
  if (savedListings.length === 0) {
    return (
      <div className="prof">
        <div className="wl-header">
          <h1 className="wl-h1">Wishlist</h1>
        </div>
        <div className="wl-empty">
          <div className="wl-empty-icon">
            <svg viewBox="0 0 64 64" width="64" height="64" fill="none">
              <path d="M32 56 L8 32 C2 26 2 16 8 10 C14 4 24 4 30 10 L32 12 L34 10 C40 4 50 4 56 10 C62 16 62 26 56 32 Z"
                    stroke="#9B9B9B" strokeWidth="2.5" strokeLinejoin="round" fill="none"/>
            </svg>
          </div>
          <h2 className="wl-empty-h2">No saved homes yet</h2>
          <p className="wl-empty-sub">
            As you browse KUNDA, tap the heart on any home to save it here.
          </p>
          <button className="btn-primary" onClick={props.onExplore}>
            Start exploring
          </button>
        </div>
      </div>
    );
  }

  /* LOGGED IN WITH SAVED LISTINGS */
  return (
    <div className="prof">
      <div className="wl-header">
        <h1 className="wl-h1">Wishlist</h1>
        <button className="wl-edit-btn" onClick={function() { setEditing(!editing); }}>
          {editing ? "Done" : "Edit"}
        </button>
      </div>

      <div className="wl-count">
        {savedListings.length} {savedListings.length === 1 ? "home" : "homes"} saved
      </div>

      <div className="grid">
        {savedListings.map(function(l, i) {
          return (
            <div key={l.id} className={"card-fade" + (l.rentalType === "long_term" ? " wl-wide" : "")} style={{ animationDelay: (i * 0.06) + "s", position: "relative" }}>
              {editing && (
                <button
                  className="wl-remove"
                  onClick={function(e) {
                    e.stopPropagation();
                    props.onSave(l.id);
                  }}
                  aria-label="Remove from wishlist"
                >
                  <IconMinus/>
                </button>
              )}
              <div style={{ pointerEvents: editing ? "none" : "auto", opacity: editing ? 0.85 : 1, transition: "opacity 0.2s" }}>
                {l.rentalType === "long_term" ? (
                  <LtCard listing={l} onTap={props.onTap} saved={true} onSave={props.onSave}/>
                ) : (
                  <ListingCard listing={l} onTap={props.onTap} saved={true} onSave={props.onSave}/>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   PROFILE PAGE
───────────────────────────────────────── */
/* ─────────────────────────────────────────
   HOST DASHBOARD PAGE — live listings
───────────────────────────────────────── */
function HostDashboardPage(props) {
  var firstName = (props.user && props.user.name) ? props.user.name.split(" ")[0] : "host";
  var hostUid = (props.user && props.user.uid) ? props.user.uid : null;
  var [myListings, setMyListings] = useState([]);
  var [busy, setBusy] = useState(true);

  useEffect(function() {
    if (!hostUid) { setBusy(false); return; }
    var qRef = query(
      collection(db, LISTINGS_COL),
      where("hostId", "==", hostUid)
    );
    var unsub = onSnapshot(qRef, function(snap) {
      var rows = snap.docs.map(function(d) { return Object.assign({ id: d.id }, d.data()); });
      /* Sort by createdAt descending (handle null) */
      rows.sort(function(a, b) {
        var ta = a.createdAt && a.createdAt.seconds ? a.createdAt.seconds : 0;
        var tb = b.createdAt && b.createdAt.seconds ? b.createdAt.seconds : 0;
        return tb - ta;
      });
      setMyListings(rows);
      setBusy(false);
    }, function(err) { console.error("Host listings:", err); setBusy(false); });
    return unsub;
  }, [hostUid]);

  var approved = myListings.filter(function(l) { return isApprovedStatus(l.status); }).length;
  var pending  = myListings.filter(function(l) { return isPendingStatus(l.status); }).length;
  var rejected = myListings.filter(function(l) { return isRejectedStatus(l.status); }).length;

  return (
    <div className="prof">
      <div className="dash-header">
        <div className="dash-tag">HOST MODE</div>
        <h1 className="dash-h1">Welcome back, <em>{firstName}</em></h1>
        <p className="dash-sub">Manage your properties and grow with KUNDA.</p>
      </div>

      {/* Status tiles: Active · Pending Review · Rejected (future-ready) */}
      <div className="dash-status-row">
        <div className="dash-status-tile">
          <div className="dash-status-num">{busy ? "—" : approved}</div>
          <div className="dash-status-lbl">Active</div>
        </div>
        <div className={"dash-status-tile" + (pending > 0 ? " is-pending" : "")}>
          <div className="dash-status-num">{busy ? "—" : pending}</div>
          <div className="dash-status-lbl">Pending review</div>
        </div>
        <div className="dash-status-tile">
          <div className="dash-status-num">{busy ? "—" : rejected}</div>
          <div className="dash-status-lbl">Rejected</div>
        </div>
      </div>

      <div className="dash-stat-row">
        <div className="dash-stat-card">
          <div className="dash-stat-icon"><IconHomeLine/></div>
          <div className="dash-stat-text">
            <div className="dash-stat-num">{busy ? "—" : myListings.length}</div>
            <div className="dash-stat-lbl">Properties listed</div>
          </div>
        </div>
      </div>

      {!busy && myListings.length > 0 && (
        <div className="dash-mini-stats">
          <div className="dash-mini">
            <div className="dash-mini-num">{approved}</div>
            <div className="dash-mini-lbl">Active</div>
          </div>
          <div className="dash-mini">
            <div className="dash-mini-num">{pending}</div>
            <div className="dash-mini-lbl">Pending review</div>
          </div>
        </div>
      )}

      <button className="dash-add-btn" onClick={props.onAddListing}>
        <IconPlus/>
        <span>Add New Listing</span>
      </button>

      {!busy && myListings.length === 0 && (
        <div className="dash-coming">
          <div className="dash-coming-icon">🏡</div>
          <h3>You haven't listed any properties yet</h3>
          <p>Tap "Add New Listing" above to share your first property with KUNDA. We'll review it within 24 hours.</p>
        </div>
      )}

      {!busy && myListings.length > 0 && (
        <div className="dash-recent">
          <div className="dash-recent-header">
            <h3>Recent listings</h3>
            <button className="dash-see-all" onClick={props.onSeeAll}>
              See all
            </button>
          </div>
          {myListings.slice(0, 2).map(function(l) {
            return (
              <HostListingRow key={l.id} listing={l} compact={true}/>
            );
          })}
        </div>
      )}

      <button className="dash-switch-back" onClick={props.onSwitchToGuest}>
        Switch to Guest Mode
      </button>
    </div>
  );
}

/* Single host listing row — used in dashboard preview and listings tab */
function HostListingRow(props) {
  var l = props.listing;
  var img = (l.photos && l.photos[0]) || l.imageUrl || FALLBACK_IMG;

  /* Determine status label/color — forgiving of typos */
  var statusLabel = "Pending review";
  var statusClass = "wait";
  if (l.hidden) {
    statusLabel = "Hidden";
    statusClass = "warn";
  } else if (isRejectedStatus(l.status)) {
    statusLabel = "Needs changes";
    statusClass = "err";
  } else if (isApprovedStatus(l.status)) {
    statusLabel = "Active";
    statusClass = "ok";
  } else if (isPendingStatus(l.status)) {
    statusLabel = "Pending review";
    statusClass = "wait";
  } else if (!l.status) {
    /* Legacy listing with no status — treat as active for now */
    statusLabel = "Active";
    statusClass = "ok";
  }

  return (
    <div className={"hl-row " + (props.compact ? "hl-compact" : "")} onClick={props.onTap}>
      <div className="hl-thumb">
        <img src={img} alt="" loading="lazy" decoding="async" onError={function(e) { e.target.src = FALLBACK_IMG; }}/>
      </div>
      <div className="hl-body">
        <div className="hl-title">{l.title}</div>
        <div className="hl-loc">{l.location}{l.code ? " · " + l.code : ""}</div>
        <div className="hl-price">GMD {Number((l.rentalType === "long_term" ? l.monthlyRent : l.pricePerNight) || 0).toLocaleString()}<span>{l.rentalType === "long_term" ? " / month" : " / night"}</span></div>
        {l.createdAt && l.createdAt.seconds ? <div className="hl-date">Listed {new Date(l.createdAt.seconds * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div> : null}
        {l.rentalType === "long_term" && <span className="hl-lt-tag">Long-term</span>}
      </div>
      <div className={"hl-status hl-status-" + statusClass}>{statusLabel}</div>
    </div>
  );
}

/* ─────────────────────────────────────────
   HOST LISTINGS PAGE — full management
───────────────────────────────────────── */
function HostListingsPage(props) {
  var hostUid = (props.user && props.user.uid) ? props.user.uid : null;
  var [myListings, setMyListings] = useState([]);
  var [busy, setBusy] = useState(true);
  var [editing, setEditing] = useState(null);   /* listing being edited */
  var [confirmDel, setConfirmDel] = useState(null);

  useEffect(function() {
    if (!hostUid) { setBusy(false); return; }
    var qRef = query(
      collection(db, LISTINGS_COL),
      where("hostId", "==", hostUid)
    );
    var unsub = onSnapshot(qRef, function(snap) {
      var rows = snap.docs.map(function(d) { return Object.assign({ id: d.id }, d.data()); });
      rows.sort(function(a, b) {
        var ta = a.createdAt && a.createdAt.seconds ? a.createdAt.seconds : 0;
        var tb = b.createdAt && b.createdAt.seconds ? b.createdAt.seconds : 0;
        return tb - ta;
      });
      setMyListings(rows);
      setBusy(false);
    }, function(err) { console.error("Host listings:", err); setBusy(false); });
    return unsub;
  }, [hostUid]);

  async function toggleHide(l) {
    try {
      await updateDoc(doc(db, LISTINGS_COL, l.id), {
        hidden:    !l.hidden,
        updatedAt: serverTimestamp(),
      });
      props.onToast(l.hidden ? "Listing visible again" : "Listing hidden");
    } catch (err) {
      console.error("Hide error:", err);
      props.onToast("Couldn't update. Try again.");
    }
  }

  async function handleDelete() {
    if (!confirmDel) return;
    try {
      await deleteDoc(doc(db, LISTINGS_COL, confirmDel.id));
      props.onToast("Listing deleted");
      setConfirmDel(null);
    } catch (err) {
      console.error("Delete error:", err);
      props.onToast("Couldn't delete. Try again.");
    }
  }

  if (!props.user) {
    return (
      <div className="prof">
        <div className="prof-header"><h1 className="prof-h1">My Listings</h1></div>
        <div className="dash-coming">
          <div className="dash-coming-icon">🔒</div>
          <h3>Log in to manage listings</h3>
          <p>You need to be logged in to view your properties.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="prof">
      <div className="prof-header">
        <h1 className="prof-h1">My Listings</h1>
      </div>

      {busy ? (
        <div className="dash-coming">
          <p style={{ color: "var(--ink-3)" }}>Loading your listings…</p>
        </div>
      ) : myListings.length === 0 ? (
        <div className="dash-coming">
          <div className="dash-coming-icon">🏡</div>
          <h3>Your first listing starts here</h3>
          <p>List your property and reach guests across The Gambia. KUNDA reviews every home before it goes live — usually within 24 hours.</p>
          <button className="btn-primary" style={{ marginTop: 20 }} onClick={props.onAddListing}>
            Add New Listing
          </button>
        </div>
      ) : (
        <>
          <div className="hl-count">
            {myListings.length} {myListings.length === 1 ? "property" : "properties"}
          </div>
          <div className="hl-list">
            {myListings.map(function(l) {
              return (
                <div key={l.id} className="hl-card">
                  <HostListingRow listing={l}/>
                  <div className="hl-actions">
                    <button className="hl-act" onClick={function() { setEditing(l); }}>
                      <IconEdit/>
                      <span>Edit</span>
                    </button>
                    <button className="hl-act" onClick={function() { toggleHide(l); }}>
                      {l.hidden ? <><IconEye/><span>Show</span></> : <><IconEyeOff/><span>Hide</span></>}
                    </button>
                    <button className="hl-act hl-act-danger" onClick={function() { setConfirmDel(l); }}>
                      <IconTrash/>
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Edit Modal */}
      {editing && (
        <EditListingModal
          listing={editing}
          onCancel={function() { setEditing(null); }}
          onSaved={function() { setEditing(null); props.onToast("Listing updated"); }}
          onError={function() { props.onToast("Couldn't save. Try again."); }}
        />
      )}

      {/* Delete confirmation */}
      {confirmDel && (
        <div className="modal-overlay" onClick={function() { setConfirmDel(null); }}>
          <div className="modal-card" onClick={function(e) { e.stopPropagation(); }}>
            <div className="modal-icon-warn">⚠️</div>
            <h2 className="modal-h2">Delete this listing?</h2>
            <p className="modal-p">
              "{confirmDel.title}" will be permanently removed from KUNDA. This cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={function() { setConfirmDel(null); }}>
                Cancel
              </button>
              <button className="modal-btn-danger" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Edit Listing Modal — title, price, description */
function EditListingModal(props) {
  var l = props.listing;
  var [title, setTitle]             = useState(l.title || "");
  var [price, setPrice]             = useState(String(l.pricePerNight || ""));
  var [description, setDescription] = useState(l.description || "");
  var [busy, setBusy]               = useState(false);

  async function handleSave() {
    if (title.trim().length < 3) return;
    if (!Number(price) || Number(price) <= 0) return;
    setBusy(true);
    try {
      var updates = {
        title:         title.trim(),
        pricePerNight: Number(price),
        description:   description.trim(),
        updatedAt:     serverTimestamp(),
      };
      if (l.rentalType === "long_term") updates.monthlyRent = Number(price);
      await updateDoc(doc(db, LISTINGS_COL, l.id), updates);
      props.onSaved();
    } catch (err) {
      console.error("Edit save:", err);
      props.onError();
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={props.onCancel}>
      <div className="modal-card modal-card-lg" onClick={function(e) { e.stopPropagation(); }}>
        <button className="modal-close" onClick={props.onCancel}><IconClose/></button>
        <h2 className="modal-h2">Edit listing</h2>
        <p className="modal-sub">Update your property details. Property code and contact stay the same.</p>

        <div className="auth-field">
          <label>Title</label>
          <input className="auth-input" value={title} onChange={function(e) { setTitle(e.target.value); }} maxLength={60}/>
        </div>

        <div className="auth-field">
          <label>{l.rentalType === "long_term" ? "Monthly rent (GMD)" : "Price (GMD per night)"}</label>
          <input className="auth-input" type="number" value={price} onChange={function(e) { setPrice(e.target.value); }}/>
        </div>

        <div className="auth-field">
          <label>Description</label>
          <textarea className="wiz-textarea" value={description} onChange={function(e) { setDescription(e.target.value); }} maxLength={400}/>
        </div>

        <div className="modal-actions">
          <button className="modal-btn-cancel" onClick={props.onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="modal-btn-primary" onClick={handleSave} disabled={busy}>
            {busy ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   PROFILE SYSTEM — launch-ready, no dead buttons
───────────────────────────────────────── */

/* Format "Member since" from auth creation time or Firestore createdAt */
function formatMemberSince(user) {
  var d = null;
  if (user.memberSince) {
    d = new Date(user.memberSince);
  } else if (user.createdAt && user.createdAt.seconds) {
    d = new Date(user.createdAt.seconds * 1000);
  }
  if (!d || isNaN(d.getTime())) return null;
  var months = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];
  return months[d.getMonth()] + " " + d.getFullYear();
}

/* Reusable sub-page header with back button */

/* Expandable FAQ item */
function FaqItem(props) {
  var [open, setOpen] = useState(false);
  return (
    <div className={"faq-item " + (open ? "is-open" : "")}>
      <button className="faq-q" onClick={function() { setOpen(!open); }}>
        <span>{props.q}</span>
        <span className="faq-chev">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="faq-a">{props.a}</div>}
    </div>
  );
}

/* ── PERSONAL INFORMATION ── */
function PersonalInfoPage(props) {
  var user = props.user;
  var [name, setName] = useState(user.name || "");
  var [phone, setPhone] = useState(user.phone || "");
  var [busy, setBusy] = useState(false);

  var changed = (name.trim() !== (user.name || "")) || (phone.trim() !== (user.phone || ""));
  var valid = name.trim().length > 0;

  async function save() {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await props.onSave({ name: name.trim(), phone: phone.trim() });
      props.onToast("Profile updated");
      props.onBack();
    } catch (e) {
      console.error(e);
      props.onToast("Couldn't save. Try again.");
      setBusy(false);
    }
  }

  return (
    <div className="subpage">
      <SubpageHeader title="Personal Information" onBack={props.onBack}/>
      <div className="subpage-body">
        <p className="subpage-intro">Manage your account details. Your contact info is never shown publicly.</p>

        <label className="field-label">Full Name</label>
        <input className="field-input" type="text" value={name}
               onChange={function(e) { setName(e.target.value); }} placeholder="Your full name"/>

        <label className="field-label">Email Address</label>
        <input className="field-input field-input-locked" type="email" value={user.email || ""} disabled/>
        <div className="field-hint">Email can't be changed</div>

        <label className="field-label">Phone Number</label>
        <input className="field-input" type="tel" inputMode="tel" value={phone}
               onChange={function(e) { setPhone(e.target.value); }} placeholder="+220 ..."/>

        <button className={"field-save " + (changed && valid ? "" : "off")}
                onClick={save} disabled={!changed || !valid || busy}>
          {busy ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

/* ── HELP CENTER ── */
function HelpCenterPage(props) {
  var [repSubject, setRepSubject] = useState("");
  var [repDesc, setRepDesc] = useState("");
  var [repBusy, setRepBusy] = useState(false);
  var [sugText, setSugText] = useState("");
  var [sugBusy, setSugBusy] = useState(false);

  async function sendFeedback(kind, payload) {
    await addDoc(collection(db, FEEDBACK_COL), Object.assign({
      kind:      kind,
      userId:    (props.user && props.user.uid) ? props.user.uid : null,
      userName:  (props.user && props.user.name) ? props.user.name : null,
      userEmail: (props.user && props.user.email) ? props.user.email : null,
      createdAt: serverTimestamp(),
    }, payload));
  }

  async function submitReport() {
    if (!repSubject.trim() || !repDesc.trim() || repBusy) return;
    setRepBusy(true);
    try {
      await sendFeedback("report", { subject: repSubject.trim(), description: repDesc.trim() });
      props.onToast("Thanks — we got your report");
      setRepSubject(""); setRepDesc("");
    } catch (e) {
      props.onToast("Couldn't send. Email support@joinkunda.com");
    }
    setRepBusy(false);
  }

  async function submitSuggestion() {
    if (!sugText.trim() || sugBusy) return;
    setSugBusy(true);
    try {
      await sendFeedback("suggestion", { description: sugText.trim() });
      props.onToast("Thanks for the suggestion!");
      setSugText("");
    } catch (e) {
      props.onToast("Couldn't send. Email support@joinkunda.com");
    }
    setSugBusy(false);
  }

  var waLink = "https://wa.me/" + KUNDA_WA + "?text=" + encodeURIComponent("Hi KUNDA, I need help with…");

  return (
    <div className="subpage">
      <SubpageHeader title="Get Help" onBack={props.onBack}/>
      <div className="subpage-body">

        <h2 className="help-h">Frequently Asked Questions</h2>
        <div className="faq-list">
          <FaqItem q="How do I book a property?"
                   a="Browse listings on the home screen, tap one you like, then tap “Book on WhatsApp.” You'll be connected directly with KUNDA to arrange your stay."/>
          <FaqItem q="How do I become a host?"
                   a="Go to your Profile and tap “Switch to Hosting,” then “+ Add Listing.” Follow the steps to list your property. We'll review it before it goes live."/>
          <FaqItem q="How do I edit my listing?"
                   a="In Host Mode, open “Listings,” find your property, and tap the edit icon to update its title, price, or description."/>
          <FaqItem q="How do I contact a host?"
                   a="All bookings and host communication are managed securely through KUNDA via WhatsApp — just tap “Book on WhatsApp” on any listing."/>
        </div>

        <h2 className="help-h">Contact Support</h2>
        <div className="help-card">
          <div className="help-contact-row">
            <span>Email</span>
            <a href="mailto:support@joinkunda.com" className="help-link">support@joinkunda.com</a>
          </div>
          <a href={waLink} target="_blank" rel="noreferrer" className="help-wa-btn">
            <WaIcon/> WhatsApp Support
          </a>
        </div>

        <h2 className="help-h">Report a Problem</h2>
        <div className="help-card">
          <input className="field-input" type="text" placeholder="Subject"
                 value={repSubject} onChange={function(e) { setRepSubject(e.target.value); }}/>
          <textarea className="field-input field-textarea" placeholder="Describe the problem…"
                    value={repDesc} onChange={function(e) { setRepDesc(e.target.value); }}/>
          <button className={"field-save " + (repSubject.trim() && repDesc.trim() ? "" : "off")}
                  onClick={submitReport} disabled={!repSubject.trim() || !repDesc.trim() || repBusy}>
            {repBusy ? "Sending…" : "Submit Report"}
          </button>
        </div>

        <h2 className="help-h">Suggest a Feature</h2>
        <div className="help-card">
          <textarea className="field-input field-textarea" placeholder="What would make KUNDA better?"
                    value={sugText} onChange={function(e) { setSugText(e.target.value); }}/>
          <button className={"field-save " + (sugText.trim() ? "" : "off")}
                  onClick={submitSuggestion} disabled={!sugText.trim() || sugBusy}>
            {sugBusy ? "Sending…" : "Submit Suggestion"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* ── TERMS & CONDITIONS ── */
function TermsPage(props) {
  return (
    <div className="subpage">
      <SubpageHeader title="Terms & Conditions" onBack={props.onBack}/>
      <div className="subpage-body">
        <div className="legal-updated">Last updated: May 2026</div>

        <div className="legal-card">
          <h2 className="legal-h">1. Booking Responsibilities</h2>
          <p>Guests must verify all property details directly with hosts through KUNDA before making any payment. Confirm availability, pricing, and accommodation details before your stay.</p>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">2. Host Responsibilities</h2>
          <p>Hosts must provide accurate, honest, and up-to-date information about their listings. Photos and descriptions must truthfully represent the property offered.</p>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">3. Payments</h2>
          <p>KUNDA is not responsible for any payments made outside of approved channels. Always arrange and confirm payment details through KUNDA's official communication.</p>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">4. Prohibited Activities</h2>
          <p>The following are strictly forbidden on KUNDA:</p>
          <ul className="legal-list">
            <li>Fake or fraudulent listings</li>
            <li>Fraud of any kind</li>
            <li>Harassment of guests or hosts</li>
            <li>Any illegal activity</li>
          </ul>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">5. Liability</h2>
          <p>KUNDA acts solely as a marketplace connecting guests and hosts. We facilitate discovery and introductions but are not a party to the rental agreement between users.</p>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">6. Contact Information</h2>
          <p>Questions about these terms? Reach us at <a href="mailto:support@joinkunda.com" className="help-link">support@joinkunda.com</a></p>
        </div>
      </div>
    </div>
  );
}

/* ── PRIVACY POLICY ── */
function PrivacyPage(props) {
  return (
    <div className="subpage">
      <SubpageHeader title="Privacy Policy" onBack={props.onBack}/>
      <div className="subpage-body">
        <div className="legal-updated">Last updated: May 2026</div>

        <div className="legal-card">
          <h2 className="legal-h">1. Information We Collect</h2>
          <ul className="legal-list">
            <li>Your name</li>
            <li>Your email address</li>
            <li>Your phone number</li>
            <li>Booking information</li>
          </ul>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">2. How Information Is Used</h2>
          <ul className="legal-list">
            <li>To improve the KUNDA platform</li>
            <li>To contact you about your account or bookings</li>
            <li>To enable booking functionality</li>
          </ul>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">3. Data Storage &amp; Security</h2>
          <p>Your data is stored securely using industry-standard infrastructure. We take reasonable measures to protect your personal information from unauthorized access.</p>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">4. Cookies</h2>
          <p>KUNDA uses basic website cookies to keep you signed in and to remember your preferences. These help the app function smoothly.</p>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">5. Your Rights</h2>
          <p>You can update your personal information any time from your profile, or request deletion of your personal data by contacting us.</p>
        </div>
        <div className="legal-card">
          <h2 className="legal-h">6. Contact Information</h2>
          <p>Privacy questions? Reach us at <a href="mailto:support@joinkunda.com" className="help-link">support@joinkunda.com</a></p>
        </div>
      </div>
    </div>
  );
}

/* ── MAIN PROFILE PAGE ── */
/* ── ACCOUNT SECURITY (P4) ─────────────────────────────────
   Verification status, change password, sign out. Google-only accounts
   have no KUNDA password to change — they're told so plainly rather than
   shown a form that can't work. */
function SecurityPage(props) {
  var user = props.user;
  var [current, setCurrent] = useState("");
  var [next, setNext] = useState("");
  var [confirm, setConfirm] = useState("");
  var [showPw, setShowPw] = useState(false);
  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState("");
  var [okMsg, setOkMsg] = useState("");
  var [delOpen, setDelOpen] = useState(false);
  var [delPassword, setDelPassword] = useState("");
  var [confirmText, setConfirmText] = useState("");
  var [delBusy, setDelBusy] = useState(false);
  var [delErr, setDelErr] = useState("");

  /* Which sign-in methods this account actually has */
  var providers = (auth.currentUser && auth.currentUser.providerData)
    ? auth.currentUser.providerData.map(function(p) { return p.providerId; })
    : [];
  var hasPassword = providers.indexOf("password") >= 0;
  var hasGoogle   = providers.indexOf("google.com") >= 0;

  async function changePassword() {
    if (busy) return;
    setErr("");
    setOkMsg("");
    if (!current) { setErr("Please enter your current password."); return; }
    if (next.length < 6) { setErr("Your new password must be at least 6 characters."); return; }
    if (next !== confirm) { setErr("The new passwords don't match."); return; }
    if (next === current) { setErr("Your new password must be different from the current one."); return; }
    setBusy(true);
    try {
      var u = auth.currentUser;
      if (!u || !u.email) throw new Error("no-user");
      /* Firebase requires a recent login before a password change */
      var cred = EmailAuthProvider.credential(u.email, current);
      await reauthenticateWithCredential(u, cred);
      await updatePassword(u, next);
      setCurrent(""); setNext(""); setConfirm("");
      setOkMsg("Your password has been changed.");
      props.onToast("Password updated 🔒");
    } catch (e) {
      var c = (e && e.code) ? e.code : "";
      console.error("Change password failed:", c);
      if (c.indexOf("wrong-password") >= 0 || c.indexOf("invalid-credential") >= 0) {
        setErr("That current password isn't right. Please try again.");
      } else if (c.indexOf("weak-password") >= 0) {
        setErr("That password is too weak. Use at least 6 characters.");
      } else if (c.indexOf("too-many-requests") >= 0) {
        setErr("Too many attempts. Please wait a few minutes and try again.");
      } else if (c.indexOf("requires-recent-login") >= 0) {
        setErr("For your security, please log out and log back in, then try again.");
      } else {
        setErr("Couldn't change your password. Please try again.");
      }
    }
    setBusy(false);
  }

  /* ── DELETE ACCOUNT (P5) ────────────────────────────────
     Soft-handling by design: listings are HIDDEN, never hard-deleted,
     because existing requests reference them — deleting would leave the
     other party staring at a broken request. Requests themselves are
     left intact for the same reason: they belong to two people, not one. */
  async function deleteAccount() {
    if (delBusy) return;
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      setDelErr('Please type DELETE to confirm.');
      return;
    }
    if (hasPassword && !delPassword) {
      setDelErr("Please enter your password to confirm.");
      return;
    }
    setDelBusy(true);
    setDelErr("");
    try {
      var u = auth.currentUser;
      if (!u) throw new Error("no-user");

      /* Firebase requires a recent login before deletion */
      if (hasPassword) {
        var cred = EmailAuthProvider.credential(u.email, delPassword);
        await reauthenticateWithCredential(u, cred);
      } else {
        await reauthenticateWithPopup(u, new GoogleAuthProvider());
      }

      var uid = u.uid;

      /* 1. Hide this host's listings — preserves every request that
            references them, and takes them off the marketplace. */
      try {
        var lsnap = await getDocs(query(collection(db, LISTINGS_COL), where("hostId", "==", uid)));
        for (var i = 0; i < lsnap.docs.length; i++) {
          /* eslint-disable-next-line no-await-in-loop */
          await updateDoc(doc(db, LISTINGS_COL, lsnap.docs[i].id), {
            hidden: true,
            updatedAt: serverTimestamp(),
          });
        }
      } catch (le) {
        console.error("Could not hide listings during deletion:", le && le.code);
      }

      /* 2. Delete the wishlist subcollection (purely personal data) */
      try {
        var wsnap = await getDocs(collection(db, USERS_COL, uid, WISHLIST_SUB));
        for (var j = 0; j < wsnap.docs.length; j++) {
          /* eslint-disable-next-line no-await-in-loop */
          await deleteDoc(doc(db, USERS_COL, uid, WISHLIST_SUB, wsnap.docs[j].id));
        }
      } catch (we) {
        console.error("Could not clear wishlist during deletion:", we && we.code);
      }

      /* 3. Delete the personal profile document */
      try {
        await deleteDoc(doc(db, USERS_COL, uid));
      } catch (pe) {
        console.error("Could not delete profile during deletion:", pe && pe.code);
      }

      /* 4. Finally the auth account itself — last, so a failure above
            never leaves an orphaned login with no profile. */
      await deleteUser(u);
      /* onAuthStateChanged fires null and returns the app to logged-out */
    } catch (e) {
      var c = (e && e.code) ? e.code : "";
      console.error("Delete account failed:", c);
      if (c.indexOf("wrong-password") >= 0 || c.indexOf("invalid-credential") >= 0) {
        setDelErr("That password isn't right. Please try again.");
      } else if (c.indexOf("requires-recent-login") >= 0) {
        setDelErr("For your security, please log out and log back in, then try again.");
      } else if (c.indexOf("popup-closed-by-user") >= 0 || c.indexOf("cancelled-popup") >= 0) {
        setDelErr("");
      } else if (c.indexOf("too-many-requests") >= 0) {
        setDelErr("Too many attempts. Please wait a few minutes and try again.");
      } else {
        setDelErr("Couldn't delete your account. Please try again or contact support.");
      }
      setDelBusy(false);
    }
  }

  return (
    <div className="subpage">
      <SubpageHeader title="Account & Security" onBack={props.onBack}/>
      <div className="subpage-body sec-body">
        {/* Status */}
        <div className="sec-card">
          <div className="sec-card-h">Account status</div>
          <div className="sec-row">
            <span>Email</span>
            <strong>{user.email || "—"}</strong>
          </div>
          <div className="sec-row">
            <span>Verification</span>
            {user.emailVerified
              ? <strong className="sec-ok">✓ Verified</strong>
              : <strong className="sec-warn">Unverified</strong>}
          </div>
          <div className="sec-row">
            <span>Sign-in method</span>
            <strong>
              {hasPassword && hasGoogle ? "Email & Google"
                : hasGoogle ? "Google"
                : "Email & password"}
            </strong>
          </div>
          {!user.emailVerified && (
            <div className="sec-note">
              Verify your email from your profile to list properties and send requests.
            </div>
          )}
        </div>

        {/* Change password — only meaningful for password accounts */}
        {hasPassword ? (
          <div className="sec-card">
            <div className="sec-card-h">Change password</div>
            <div className="auth-field">
              <label>Current password</label>
              <input className="auth-input" type={showPw ? "text" : "password"}
                     value={current} autoComplete="current-password"
                     onChange={function(e) { setCurrent(e.target.value); }}/>
            </div>
            <div className="auth-field">
              <label>New password</label>
              <input className="auth-input" type={showPw ? "text" : "password"}
                     value={next} autoComplete="new-password"
                     onChange={function(e) { setNext(e.target.value); }}/>
            </div>
            <div className="auth-field">
              <label>Confirm new password</label>
              <input className="auth-input" type={showPw ? "text" : "password"}
                     value={confirm} autoComplete="new-password"
                     onChange={function(e) { setConfirm(e.target.value); }}/>
            </div>
            <button type="button" className="sec-show" onClick={function() { setShowPw(!showPw); }}>
              {showPw ? "Hide passwords" : "Show passwords"}
            </button>

            {err && <div className="auth-err">{err}</div>}
            {okMsg && <div className="auth-notice">{okMsg}</div>}

            <button className="sec-save" onClick={changePassword} disabled={busy}>
              {busy ? "Updating…" : "Update password"}
            </button>
          </div>
        ) : (
          <div className="sec-card">
            <div className="sec-card-h">Password</div>
            <div className="sec-note">
              You sign in to KUNDA with Google, so there's no KUNDA password to change.
              Your password is managed in your Google account.
            </div>
          </div>
        )}

        {/* Sign out */}
        <div className="sec-card">
          <div className="sec-card-h">Session</div>
          <button className="sec-signout" onClick={props.onLogout}>Sign out of KUNDA</button>
        </div>

        {/* Danger zone — deliberately last, deliberately two-step */}
        <div className="sec-card sec-danger">
          <div className="sec-card-h">Delete account</div>
          {!delOpen ? (
            <>
              <div className="sec-note sec-note-top">
                Permanently deletes your KUNDA profile and saved homes. Your listings are
                removed from the marketplace, and past requests stay with the people you
                dealt with. This cannot be undone.
              </div>
              <button className="sec-danger-btn" onClick={function() { setDelOpen(true); setDelErr(""); }}>
                Delete my account
              </button>
            </>
          ) : (
            <>
              <div className="sec-note sec-note-top">
                This is permanent. To confirm, type <strong>DELETE</strong> below
                {hasPassword ? " and enter your password." : " and confirm with Google."}
              </div>
              <div className="auth-field">
                <label>Type DELETE to confirm</label>
                <input className="auth-input" value={confirmText} autoCapitalize="characters"
                       placeholder="DELETE"
                       onChange={function(e) { setConfirmText(e.target.value); }}/>
              </div>
              {hasPassword && (
                <div className="auth-field">
                  <label>Your password</label>
                  <input className="auth-input" type="password" value={delPassword}
                         autoComplete="current-password"
                         onChange={function(e) { setDelPassword(e.target.value); }}/>
                </div>
              )}
              {delErr && <div className="auth-err">{delErr}</div>}
              <div className="sec-danger-actions">
                <button className="sec-cancel-btn" disabled={delBusy}
                        onClick={function() {
                          setDelOpen(false); setConfirmText(""); setDelPassword(""); setDelErr("");
                        }}>
                  Keep my account
                </button>
                <button className="sec-danger-btn" disabled={delBusy} onClick={deleteAccount}>
                  {delBusy ? "Deleting…" : "Permanently delete"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfilePage(props) {
  var user = props.user;
  var [view, setView] = useState("menu");      /* menu | personal | security | help | terms | privacy */
  var [showLogout, setShowLogout] = useState(false);
  var [vBusy, setVBusy] = useState("");        /* "send" | "check" while working */
  var [vMsg, setVMsg] = useState("");

  /* Resend the verification email */
  async function resendVerification() {
    if (vBusy) return;
    setVBusy("send");
    setVMsg("");
    try {
      if (!auth.currentUser) throw new Error("no-user");
      await sendEmailVerification(auth.currentUser);
      setVMsg("Verification email sent. Check your inbox — and your spam folder.");
    } catch (e) {
      var c = (e && e.code) ? e.code : "";
      console.error("Resend verification failed:", c);
      setVMsg(c.indexOf("too-many-requests") >= 0
        ? "Too many attempts. Please wait a few minutes and try again."
        : "Couldn't send the email. Please check your connection and try again.");
    }
    setVBusy("");
  }

  /* Firebase caches emailVerified on the local token — after the person
     clicks the link elsewhere we must reload the user to see the change. */
  async function checkVerification() {
    if (vBusy) return;
    setVBusy("check");
    setVMsg("");
    try {
      if (!auth.currentUser) throw new Error("no-user");
      await reload(auth.currentUser);
      if (auth.currentUser.emailVerified) {
        setVMsg("");
        if (props.onVerified) props.onVerified();
        props.onToast("Email verified — you're all set 🌿");
      } else {
        setVMsg("Not verified yet. Open the link in the email we sent, then tap this again.");
      }
    } catch (e) {
      console.error("Verification check failed:", e && e.code);
      setVMsg("Couldn't check right now. Please try again.");
    }
    setVBusy("");
  }

  /* Deep-link from the footer (Terms / Privacy / Help) */
  useEffect(function() {
    if (props.jumpTo) {
      setView(props.jumpTo);
      if (props.onJumped) props.onJumped();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.jumpTo]);

  /* LOGGED OUT VIEW */
  if (!user) {
    return (
      <div className="prof">
        <div className="prof-header">
          <h1 className="prof-h1">Profile</h1>
        </div>
        <div className="auth-prompt">
          <div className="auth-prompt-icon">
            <img src={kundaLogo} alt="KUNDA" className="auth-prompt-logo-img"/>
          </div>
          <h2 className="auth-prompt-h2">Log in to KUNDA</h2>
          <p className="auth-prompt-sub">
            Save your favourite homes, manage your bookings, and host your property — all in one place.
          </p>
          <button className="btn-primary auth-prompt-btn" onClick={props.onLogin}>
            Log in or sign up
          </button>
        </div>
      </div>
    );
  }

  /* SUB-PAGES */
  if (view === "personal") {
    return <PersonalInfoPage user={user} onSave={props.onUpdateProfile} onToast={props.onToast}
                             onBack={function() { setView("menu"); }}/>;
  }
  if (view === "help") {
    return <HelpCenterPage user={user} onToast={props.onToast}
                           onBack={function() { setView("menu"); }}/>;
  }
  if (view === "security") {
    return <SecurityPage user={user}
                         onToast={props.onToast}
                         onLogout={function() {
                           /* The confirmation modal is rendered by the menu
                              view, so return there before opening it —
                              otherwise the state flips with nothing on
                              screen to show it. */
                           setView("menu");
                           setShowLogout(true);
                         }}
                         onBack={function() { setView("menu"); }}/>;
  }
  if (view === "terms") {
    return <TermsPage onBack={function() { setView("menu"); }}/>;
  }
  if (view === "privacy") {
    return <PrivacyPage onBack={function() { setView("menu"); }}/>;
  }

  /* MAIN MENU */
  var displayName = user.name || "KUNDA member";
  var initial = (user.name || user.email || "?").charAt(0).toUpperCase();
  var isHost = props.mode === "host";
  var memberSince = formatMemberSince(user);

  return (
    <div className="prof">
      <div className="prof-header">
        <h1 className="prof-h1">Profile</h1>
      </div>

      {/* Identity card */}
      <div className="me-card-v2">
        <div className="me-avatar-lg">{initial}</div>
        <div className="me-info">
          <div className="me-name-v2">{displayName}</div>
          <div className="me-status">
            {isHost ? "Host" : "Guest"}
            {user.emailVerified === true && <span className="me-verified">✓ Verified</span>}
          </div>
          {memberSince && <div className="me-since">Member since {memberSince}</div>}
        </div>
      </div>

      {/* Email verification — informative, never blocks browsing */}
      {REQUIRE_EMAIL_VERIFICATION && user.emailVerified === false && (
        <div className="verify-banner">
          <div className="verify-top">
            <span className="verify-badge">Unverified</span>
            <span className="verify-title">Verify your email</span>
          </div>
          <p className="verify-text">
            You can browse and save homes freely. To list a property or send a request,
            confirm your email address first — we sent a link to <strong>{user.email}</strong>.
          </p>
          {vMsg && <div className="verify-msg">{vMsg}</div>}
          <div className="verify-actions">
            <button className="verify-btn-primary" onClick={checkVerification} disabled={!!vBusy}>
              {vBusy === "check" ? "Checking…" : "I've verified"}
            </button>
            <button className="verify-btn-ghost" onClick={resendVerification} disabled={!!vBusy}>
              {vBusy === "send" ? "Sending…" : "Resend email"}
            </button>
          </div>
        </div>
      )}

      {/* Switch mode — black premium pill */}
      <button className="host-toggle-pill" onClick={props.onToggleMode}>
        <IconHostKey/>
        <span>{isHost ? "Switch to Guest Mode" : "Switch to Hosting"}</span>
      </button>

      {/* Menu list */}
      <div className="prof-menu">
        <button className="prof-menu-row" onClick={function() { setView("personal"); }}>
          <div className="prof-menu-icon"><IconUser/></div>
          <div className="prof-menu-text">
            <div className="prof-menu-label">Personal Information</div>
            <div className="prof-menu-sub">Manage your account details</div>
          </div>
          <div className="prof-menu-chev"><IconChevronR/></div>
        </button>
        <button className="prof-menu-row" onClick={function() { setView("security"); }}>
          <div className="prof-menu-icon"><IconLock/></div>
          <div className="prof-menu-text">
            <div className="prof-menu-label">Account &amp; Security</div>
            <div className="prof-menu-sub">
              {user.emailVerified ? "Verified · password and sign out" : "Unverified · password and sign out"}
            </div>
          </div>
          <div className="prof-menu-chev"><IconChevronR/></div>
        </button>
        <button className="prof-menu-row" onClick={function() { setView("help"); }}>
          <div className="prof-menu-icon"><IconHelp/></div>
          <div className="prof-menu-text">
            <div className="prof-menu-label">Get Help</div>
            <div className="prof-menu-sub">Contact support and find answers</div>
          </div>
          <div className="prof-menu-chev"><IconChevronR/></div>
        </button>
        <button className="prof-menu-row" onClick={function() { setView("terms"); }}>
          <div className="prof-menu-icon"><IconDoc/></div>
          <div className="prof-menu-text">
            <div className="prof-menu-label">Terms &amp; Conditions</div>
            <div className="prof-menu-sub">Read our platform rules</div>
          </div>
          <div className="prof-menu-chev"><IconChevronR/></div>
        </button>
        <button className="prof-menu-row" onClick={function() { setView("privacy"); }}>
          <div className="prof-menu-icon"><IconLock/></div>
          <div className="prof-menu-text">
            <div className="prof-menu-label">Privacy Policy</div>
            <div className="prof-menu-sub">Learn how we protect your data</div>
          </div>
          <div className="prof-menu-chev"><IconChevronR/></div>
        </button>
        <button className="prof-menu-row prof-menu-row-danger" onClick={function() { setShowLogout(true); }}>
          <div className="prof-menu-icon"><IconLogout/></div>
          <div className="prof-menu-text">
            <div className="prof-menu-label">Log Out</div>
            <div className="prof-menu-sub">Sign out of your account</div>
          </div>
          <div className="prof-menu-chev"><IconChevronR/></div>
        </button>
      </div>

      {/* Logout confirmation modal */}
      {showLogout && (
        <div className="modal-overlay" onClick={function() { setShowLogout(false); }}>
          <div className="modal-card" onClick={function(e) { e.stopPropagation(); }}>
            <h3 className="modal-title">Log Out?</h3>
            <p className="modal-text">You will need to sign in again to access your account.</p>
            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={function() { setShowLogout(false); }}>
                Cancel
              </button>
              <button className="modal-btn-danger" onClick={function() { setShowLogout(false); props.onLogout(); }}>
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   ROOT APP
───────────────────────────────────────── */
export default function App() {
  /* App mode — "guest" or "host". Initial value read from localStorage. */
  var initialMode = "guest";
  try {
    var savedMode = localStorage.getItem(APP_MODE_KEY);
    if (savedMode === "host" || savedMode === "guest") initialMode = savedMode;
  } catch (e) { /* ignore */ }

  /* Firebase Auth needs a moment to restore a persisted session on load.
     Until onAuthStateChanged fires we are NOT "signed out" — we are unknown.
     Listeners that need a uid must wait for this to become false. */
  var [authLoading, setAuthLoading] = useState(true);
  /* Declared here, above every effect that reads it. It previously sat far
     below the requests listener — legal at runtime thanks to var hoisting,
     but no-use-before-define flags it, and Vercel builds with CI=true where
     warnings become errors. */
  var [user, setUser]         = useState(null);
  var [mode, setModeState] = useState(initialMode);
  var [tab, setTab]           = useState(initialMode === "host" ? "dashboard" : "explore");
  var [detail, setDetail]     = useState(null);
  /* Browsing context lives HERE, not inside ExplorePage — that component
     unmounts whenever a listing detail opens, which is exactly why Back
     used to drop the guest back into Short Stay. */
  var [category, setCategory] = useState("home");   /* home | long_term | services */
  /* Where the guest was when they entered Host Mode. */
  var [guestReturnTab, setGuestReturnTab] = useState("explore");
  var [profileJump, setProfileJump] = useState(null);  /* deep-link into a profile sub-page */
  function openPolicy(v) { closeDetail(); setProfileJump(v); setTab("profile"); window.scrollTo(0, 0); }

  /* Hosting requires an account — listings must belong to a real host
     (security rules enforce hostId == auth.uid on create). */
  function startHosting() {
    if (!user) {
      showToast("Log in to list your home");
      openAuth();
      return;
    }
    if (REQUIRE_EMAIL_VERIFICATION && !user.emailVerified) {
      showToast("Please verify your email before listing a property");
      switchTab("profile");
      return;
    }
    setHosting("choose");
  }

  /* ── Deep linking: open/close a property and keep the URL in sync ── */
  /* Where the feed was scrolled when a property was opened, so closing the
     property returns the person to exactly where they were browsing rather
     than dumping them at the top of the list. */
  var feedScrollRef = useRef(0);

  function openDetail(l) {
    if (l.rentalType !== "long_term") addRecentView(l);
    feedScrollRef.current = window.scrollY || 0;
    setDetail(l);
    window.scrollTo(0, 0);
    pushPropertyUrl(l.id);
  }
  function closeDetail() {
    var y = feedScrollRef.current || 0;
    setDetail(null);
    pushHomeUrl();
    /* Restore after the feed has painted, otherwise the page isn't tall
       enough yet to scroll to and the position is silently clamped to 0. */
    requestAnimationFrame(function() {
      requestAnimationFrame(function() { window.scrollTo(0, y); });
    });
  }

  /* Per-property SEO: title, meta description, canonical URL (Google runs JS) */
  useEffect(function() {
    var desc = document.querySelector('meta[name="description"]');
    var canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    if (detail) {
      var t = (detail.title && detail.title.trim()) ? detail.title.trim() : autoTitle(detail.propertyType, detail.location);
      document.title = t + " — KUNDA";
      var isLT = detail.rentalType === "long_term";
      if (desc) desc.setAttribute("content",
        (isLT ? "Rent " : "Book ") + t + " in " + (detail.location || "The Gambia") + " on KUNDA. GMD " +
        (Number(isLT ? detail.monthlyRent : detail.pricePerNight) || 0).toLocaleString() +
        (isLT ? " per month." : " per night, verified and booked via WhatsApp."));
      canon.setAttribute("href", SITE_URL + "/property/" + encodeURIComponent(detail.id));
    } else {
      document.title = "KUNDA - Short-stay homes in The Gambia";
      if (desc) desc.setAttribute("content",
        "KUNDA - the trusted way to find and book short-stay homes in The Gambia. Browse verified homes, see photos, and book directly on WhatsApp.");
      canon.setAttribute("href", SITE_URL + "/");
    }
  }, [detail]);
  var [hosting, setHosting]   = useState(false);
  var [authOpen, setAuthOpen] = useState(false);
  var [saved, setSaved]       = useState([]);
  var [toast, setToast]       = useState(null);
  var [listings, setListings] = useState([]);
  var [loading, setLoading]   = useState(true);
  var [loadError, setLoadError] = useState(false);
  var [reloadKey, setReloadKey] = useState(0);

  /* ── REQUESTS (Long-Term connection loop) ── */
  var [guestRequests, setGuestRequests] = useState([]);
  var [hostRequests, setHostRequests] = useState([]);
  var [bellOpen, setBellOpen] = useState(false);
  var [requestSheet, setRequestSheet] = useState(null);   /* { listing, type } */
  var [requestDetail, setRequestDetail] = useState(null); /* request being viewed */

  useEffect(function() {
    /* Don't decide anything while Firebase Auth is still restoring the session */
    if (authLoading) return;
    if (!user || !user.uid) {
      setGuestRequests([]);
      setHostRequests([]);
      return;
    }
    var qG = query(collection(db, "requests"), where("guestId", "==", user.uid));
    var unsubG = onSnapshot(qG, function(snap) {
      setGuestRequests(snap.docs.map(function(d) { return Object.assign({ id: d.id }, d.data()); }));
    }, function(err) { console.error("Guest requests error:", err); });
    var qH = query(collection(db, "requests"), where("hostId", "==", user.uid));
    var unsubH = onSnapshot(qH, function(snap) {
      setHostRequests(snap.docs.map(function(d) { return Object.assign({ id: d.id }, d.data()); }));
    }, function(err) { console.error("Host requests error:", err); });
    return function() { unsubG(); unsubH(); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [authLoading, user && user.uid]);

  /* Keep the open Request Detail in sync with live snapshot updates */
  useEffect(function() {
    if (!requestDetail) return;
    var fresh = guestRequests.find(function(r) { return r.id === requestDetail.id; });
    if (fresh && fresh !== requestDetail) setRequestDetail(fresh);
  }, [guestRequests, requestDetail]);

  var guestUnread = guestRequests.filter(function(r) { return r.unseenGuest; }).length;
  var hostUnread = hostRequests.filter(function(r) { return r.unseenHost; }).length;

  function clearUnseen(list, field) {
    list.forEach(function(r) {
      if (r[field]) {
        var u = {}; u[field] = false;
        updateDoc(doc(db, "requests", r.id), u).catch(function() {});
      }
    });
  }
  function openBell() {
    if (mode === "host" && hostUnread > 0) {
      clearUnseen(hostRequests, "unseenHost");
      switchTab("requests");
      return;
    }
    clearUnseen(guestRequests, "unseenGuest");
    setBellOpen(true);
  }
  /* Called after the profile screen confirms verification succeeded —
     updates the React user so the gates unlock without a reload. */
  function markVerified() {
    setUser(function(u) {
      return u ? Object.assign({}, u, { emailVerified: true }) : u;
    });
  }

  function openRequestSheet(l, type) {
    if (!user) {
      showToast("Log in to send a request");
      openAuth();
      return;
    }
    if (REQUIRE_EMAIL_VERIFICATION && !user.emailVerified) {
      showToast("Please verify your email before sending a request");
      switchTab("profile");
      return;
    }
    setRequestSheet({ listing: l, type: type });
  }
  /* Open the property from a shared /property/{id} URL once listings arrive.
     (Must be declared AFTER `listings` so the dependency array sees it.) */
  var deepLinkDone = useRef(false);
  useEffect(function() {
    if (deepLinkDone.current) return;
    var id = parsePropertyId();
    if (!id) { deepLinkDone.current = true; return; }
    if (!listings || listings.length === 0) return;   /* wait for data */
    deepLinkDone.current = true;
    var found = listings.find(function(l) { return l.id === id; });
    if (found) {
      addRecentView(found);
      setDetail(found);
      window.scrollTo(0, 0);
    } else {
      /* Listing removed or not yet approved — land on the homepage gracefully */
      try { window.history.replaceState({}, "", "/"); } catch (e) {}
    }
  }, [listings]);

  /* Browser back/forward navigates between homepage and property pages */
  useEffect(function() {
    function onPop() {
      var id = parsePropertyId();
      if (!id) { setDetail(null); window.scrollTo(0, 0); return; }
      var found = (listings || []).find(function(l) { return l.id === id; });
      if (found) { setDetail(found); window.scrollTo(0, 0); }
    }
    window.addEventListener("popstate", onPop);
    return function() { window.removeEventListener("popstate", onPop); };
  }, [listings]);

  /* Mode setter — persists to localStorage and switches to mode's default tab */
  function setMode(newMode) {
    setModeState(newMode);
    try { localStorage.setItem(APP_MODE_KEY, newMode); } catch (e) {}
    setDetail(null);
    pushHomeUrl();
    /* Entering Host Mode always opens the dashboard. Leaving it returns to
       the screen the guest came from — usually Profile, since that is where
       the switch lives — instead of resetting them to the home feed. */
    setTab(newMode === "host" ? "dashboard" : (guestReturnTab || "explore"));
    window.scrollTo(0, 0);
  }

  /* Completes a signInWithRedirect round-trip (used when a browser blocks
     the Google popup). onAuthStateChanged handles the success path; this
     only surfaces errors that would otherwise vanish after the redirect. */
  useEffect(function() {
    getRedirectResult(auth).catch(function(e) {
      if (e && e.code) console.error("Google redirect sign-in error:", e.code);
    });
  }, []);

  /* Auth state listener — runs once on mount */
  useEffect(function() {
    var unsub = onAuthStateChanged(auth, async function(fbUser) {
      if (fbUser) {
        /* A "host" mode restored from localStorage must not survive if the
           account isn't verified — otherwise a reload bypasses the button
           guard and lands an unverified user straight in Host Mode. */
        if (!effectiveVerified(fbUser)) {
          setModeState("guest");
          try { localStorage.setItem(APP_MODE_KEY, "guest"); } catch (e) {}
        }

        /* Identity is already known here — publish it to the app IMMEDIATELY.
           Previously setUser() sat behind an awaited Firestore read, so a slow
           or failing connection left user = null indefinitely (auth listeners
           and anything keyed on user.uid never ran). The profile read below
           still enriches this object once it returns. */
        setUser({
          uid:           fbUser.uid,
          name:          fbUser.displayName || "",
          email:         fbUser.email || "",
          emailVerified: effectiveVerified(fbUser),
        });

        /* User is signed in — load their Firestore profile */
        try {
          var memberSince = (fbUser.metadata && fbUser.metadata.creationTime) ? fbUser.metadata.creationTime : null;
          var userDoc = await getDoc(doc(db, USERS_COL, fbUser.uid));
          if (userDoc.exists()) {
            setUser(Object.assign({ uid: fbUser.uid, memberSince: memberSince }, userDoc.data(), { emailVerified: effectiveVerified(fbUser) }));
          } else {
            /* No profile doc yet (older account or interrupted signup) — create one */
            var profile = {
              uid:       fbUser.uid,
              name:      fbUser.displayName || "",
              email:     fbUser.email || "",
              isHost:    false,
              createdAt: serverTimestamp(),
            };
            await setDoc(doc(db, USERS_COL, fbUser.uid), profile);
            setUser(Object.assign({}, profile, { emailVerified: effectiveVerified(fbUser) }));
          }

          /* Read any guest-mode wishlist from localStorage */
          var guestSaved = [];
          try {
            var raw = localStorage.getItem(GUEST_WISHLIST_KEY);
            if (raw) guestSaved = JSON.parse(raw) || [];
          } catch (e) { guestSaved = []; }

          /* Load wishlist from Firestore subcollection */
          var wlRef  = collection(db, USERS_COL, fbUser.uid, WISHLIST_SUB);
          var wlSnap = await getDocs(wlRef);
          var cloudSaved = wlSnap.docs.map(function(d) { return d.id; });

          /* Merge: any guest saves that aren't already in cloud → write to cloud */
          var toMerge = guestSaved.filter(function(id) { return cloudSaved.indexOf(id) < 0; });
          if (toMerge.length > 0) {
            await Promise.all(toMerge.map(function(id) {
              return setDoc(doc(db, USERS_COL, fbUser.uid, WISHLIST_SUB, id), {
                listingId: id,
                savedAt:   serverTimestamp(),
              });
            }));
          }

          /* Final list = union of cloud + merged guest items */
          var merged = cloudSaved.slice();
          toMerge.forEach(function(id) { merged.push(id); });
          setSaved(merged);

          /* Clear localStorage — wishlist now lives in cloud */
          try { localStorage.removeItem(GUEST_WISHLIST_KEY); } catch (e) {}
        } catch (err) {
          console.error("Error loading user profile or wishlist:", err);
          setUser({
            uid:   fbUser.uid,
            name:  fbUser.displayName || "",
            email: fbUser.email || "",
          });
        }
        /* Close auth screen if it was open */
        setAuthOpen(false);
      } else {
        /* Firebase has explicitly confirmed there is no signed-in user.
           onAuthStateChanged is the single source of truth: a "host" mode
           left in localStorage must not keep the host UI on screen without
           a real authenticated session. */
        setUser(null);
        if (mode === "host") {
          setModeState("guest");
          setTab("explore");
          try { localStorage.setItem(APP_MODE_KEY, "guest"); } catch (e) {}
        }
        try {
          var raw2 = localStorage.getItem(GUEST_WISHLIST_KEY);
          setSaved(raw2 ? (JSON.parse(raw2) || []) : []);
        } catch (e) { setSaved([]); }
      }
      /* Auth has now resolved one way or the other — listeners may proceed */
      setAuthLoading(false);
    });
    return unsub;
    /* Mounts once by design. `mode` is read inside only to drop a stale
       host session; adding it here would re-subscribe the auth listener on
       every mode switch. */
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  /* Live Firestore — only approved listings */
  useEffect(function() {
    var ref = collection(db, LISTINGS_COL);
    /* For now, show all listings ordered by date.
       To filter only approved: query(ref, where("status", "==", "approved"), orderBy("createdAt", "desc"))
       BUT this requires a composite index. So we'll filter client-side. */
    var qRef = query(ref, orderBy("createdAt", "desc"), limit(250));

    var unsub = onSnapshot(
      qRef,
      function(snap) {
        var all = snap.docs.map(function(d) { return Object.assign({ id: d.id }, d.data()); });
        /* Show approved listings + any without a status field (legacy) — exclude hidden */
        var visible = all.filter(function(l) {
          if (l.hidden === true) return false;
          return isApprovedStatus(l.status) || !l.status;
        });
        setListings(visible);
        setLoadError(false);
        setLoading(false);
      },
      function(err) {
        /* A failed load must never look like an empty marketplace — surface it
           so the guest can retry instead of assuming KUNDA has no homes. */
        console.error("Firestore error:", err);
        setLoadError(true);
        setLoading(false);
      }
    );
    return unsub;
  }, [reloadKey]);

  /* Re-subscribe to the listings query (used by the error state's Retry) */
  function retryLoad() {
    setLoadError(false);
    setLoading(true);
    setReloadKey(function(k) { return k + 1; });
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(function() { setToast(null); }, 3000);
  }

  /* Persist wishlist toggle:
     - Logged in → write/delete in users/{uid}/wishlist/{listingId}
     - Guest    → save to localStorage (carries over on signup) */
  async function toggleSave(id) {
    var had = saved.includes(id);
    var newSaved = had ? saved.filter(function(x) { return x !== id; }) : saved.concat([id]);

    /* Optimistic UI update — feels instant */
    setSaved(newSaved);
    showToast(had ? "Removed" : "Saved ❤️");

    if (user && user.uid) {
      /* Logged in — persist to Firestore */
      try {
        var ref = doc(db, USERS_COL, user.uid, WISHLIST_SUB, id);
        if (had) {
          await deleteDoc(ref);
        } else {
          await setDoc(ref, {
            listingId: id,
            savedAt:   serverTimestamp(),
          });
        }
      } catch (err) {
        console.error("Wishlist save error:", err);
        /* Revert on error */
        setSaved(saved);
        showToast("Couldn't save. Try again.");
      }
    } else {
      /* Guest — persist to localStorage */
      try {
        localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(newSaved));
      } catch (err) {
        console.error("Local save error:", err);
      }
    }
  }

  function switchTab(t) {
    setDetail(null);
    pushHomeUrl();
    setHosting(false);
    setTab(t);
    window.scrollTo(0, 0);
  }

  /* HOST WIZARD VIEW — chooser routes to Short-Stay or Long-Term */
  if (hosting === "choose") {
    return (
      <ListingTypeChooser
        onPick={function(p) { setHosting(p === "long" ? "long" : "short"); }}
        onCancel={function() { setHosting(false); }}
      />
    );
  }
  if (hosting === "long") {
    return (
      <LongTermWizard
        user={user}
        onCancel={function() { setHosting(false); }}
        onDone={function() {
          setHosting(false);
          setTab(mode === "host" ? "dashboard" : "explore");
        }}
      />
    );
  }
  if (hosting) {
    return (
      <HostWizard
        user={user}
        onCancel={function() { setHosting(false); }}
        onDone={function() {
          setHosting(false);
          setTab(mode === "host" ? "dashboard" : "explore");
        }}
      />
    );
  }

  /* AUTH SCREEN VIEW */
  if (authOpen) {
    return (
      <AuthScreen onCancel={function() { setAuthOpen(false); }}/>
    );
  }

  async function handleLogout() {
    try {
      await signOut(auth);
      showToast("Logged out");
      /* Reset to guest mode on logout */
      setMode("guest");
    } catch (err) {
      console.error("Logout error:", err);
      showToast("Could not log out. Try again.");
    }
  }

  /* Update the signed-in user's profile (name + phone) in Firestore + local state */
  async function handleUpdateProfile(updates) {
    if (!user || !user.uid) throw new Error("Not signed in");
    await updateDoc(doc(db, USERS_COL, user.uid), {
      name:      updates.name,
      phone:     updates.phone,
      updatedAt: serverTimestamp(),
    });
    setUser(Object.assign({}, user, { name: updates.name, phone: updates.phone }));
  }

  function openAuth() {
    setAuthOpen(true);
  }

  /* Toggle between guest and host mode — requires login for host mode */
  function toggleMode() {
    if (!user) {
      showToast("Please log in to host");
      openAuth();
      return;
    }
    var newMode = mode === "host" ? "guest" : "host";
    if (newMode === "host") setGuestReturnTab(tab);
    /* Entering Host Mode requires a verified email — the same guard as
       startHosting(). Leaving Host Mode is always allowed. */
    if (REQUIRE_EMAIL_VERIFICATION && newMode === "host" && !user.emailVerified) {
      showToast("Please verify your email before hosting on KUNDA");
      switchTab("profile");
      return;
    }
    setMode(newMode);
    showToast(newMode === "host" ? "Welcome to Host Mode 🏡" : "Switched to Guest Mode");
  }

  /* User avatar initial for navbar */
  var avatarInitial = user ? (user.name || user.email || "?").charAt(0).toUpperCase() : "";

  return (
    <div className="app">
      {/* TOP BAR (only when not on detail) */}
      {!detail && (
        <header className="navbar">
          <div className="navbar-in">
            <div className="logo" onClick={function() { switchTab(mode === "host" ? "dashboard" : "explore"); }}>
              <img src={kundaLogo} alt="" className="logo-img"/>
              <span className="logo-wordmark">KUNDA</span>
            </div>
            <div className="nav-r">
              {user && <BellButton count={guestUnread + hostUnread} onClick={openBell}/>}
              {mode === "guest" && (
                <button className="btn-host-pill" onClick={function() { startHosting(); }}>
                  Host your property
                </button>
              )}
              {user ? (
                <div className="nav-av nav-av-user" onClick={function() { switchTab("profile"); }}>
                  <span>{avatarInitial}</span>
                </div>
              ) : (
                <div className="nav-av" onClick={function() { switchTab("profile"); }}>
                  <IconUser/>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* MAIN CONTENT */}
      <main className="content">
        <div className="page-fade" key={detail ? ("detail-" + detail.id) : ("tab-" + tab)}>
        {detail ? (
          detail.rentalType === "long_term" ? (
          <LongTermDetail
            listing={detail}
            saved={saved.includes(detail.id)}
            onSave={toggleSave}
            onToast={showToast}
            onRequest={openRequestSheet}
            onBack={closeDetail}
          />
          ) : (
          <DetailPage
            listing={detail}
            saved={saved.includes(detail.id)}
            onSave={toggleSave}
            onBack={closeDetail}
          />
          )
        ) : mode === "host" ? (
          /* HOST MODE TABS */
          tab === "dashboard" ? (
            <HostDashboardPage
              user={user}
              onAddListing={function() { startHosting(); }}
              onSwitchToGuest={toggleMode}
              onSeeAll={function() { switchTab("listings"); }}
            />
          ) : tab === "listings" ? (
            <HostListingsPage
              user={user}
              onAddListing={function() { startHosting(); }}
              onToast={showToast}
            />
          ) : tab === "requests" ? (
            <HostRequestsPanel requests={hostRequests} onToast={showToast}/>
          ) : (
            <ProfilePage
              user={user}
              jumpTo={profileJump}
              onJumped={function() { setProfileJump(null); }}
              mode={mode}
              onToggleMode={toggleMode}
              onStartHost={function() { startHosting(); }}
              onUpdateProfile={handleUpdateProfile}
              onVerified={markVerified}
              onToast={showToast}
              onLogin={openAuth}
              onLogout={handleLogout}
            />
          )
        ) : (
          /* GUEST MODE TABS */
          tab === "explore" ? (
            <ExplorePage
              listings={listings}
              loading={loading}
              loadError={loadError}
              category={category}
              onCategory={setCategory}
              onRetry={retryLoad}
              onTap={function(l) { openDetail(l); }}
              saved={saved}
              onSave={toggleSave}
              onBecomeHost={function() { startHosting(); }}
            />
          ) : tab === "wishlist" ? (
            <WishlistPage
              user={user}
              listings={listings}
              saved={saved}
              onSave={toggleSave}
              onTap={function(l) { openDetail(l); }}
              onLogin={openAuth}
              onExplore={function() { switchTab("explore"); }}
            />
          ) : (
            <ProfilePage
              user={user}
              jumpTo={profileJump}
              onJumped={function() { setProfileJump(null); }}
              mode={mode}
              onToggleMode={toggleMode}
              onStartHost={function() { startHosting(); }}
              onUpdateProfile={handleUpdateProfile}
              onVerified={markVerified}
              onToast={showToast}
              onLogin={openAuth}
              onLogout={handleLogout}
            />
          )
        )}
        </div>

        {/* ── REQUEST LOOP OVERLAYS ── */}
        {requestSheet && user && (
          <RequestSheet
            listing={requestSheet.listing}
            type={requestSheet.type}
            user={user}
            myRequests={guestRequests}
            onClose={function() { setRequestSheet(null); }}
            onViewRequests={function() {
              setRequestSheet(null);
              clearUnseen(guestRequests, "unseenGuest");
              setBellOpen(true);
            }}
            onToast={showToast}
          />
        )}
        {bellOpen && (
          <NotificationsPanel
            requests={guestRequests}
            onClose={function() { setBellOpen(false); }}
            onOpenRequest={function(r) {
              setBellOpen(false);
              setRequestDetail(r);
              window.scrollTo(0, 0);
            }}
          />
        )}
        {requestDetail && (
          <RequestDetail
            request={requestDetail}
            onBack={function() { setRequestDetail(null); }}
          />
        )}
      </main>

      {/* BOTTOM NAV — adapts to mode */}
      {!detail && (
        <nav className="bnav">
          {mode === "host" ? (
            <>
              <button className={"bni " + (tab === "dashboard" ? "on" : "")}
                      onClick={function() { switchTab("dashboard"); }}>
                <IconDashboard/>
                <span>Dashboard</span>
              </button>
              <button className={"bni " + (tab === "listings" ? "on" : "")}
                      onClick={function() { switchTab("listings"); }}>
                <IconHomes/>
                <span>Listings</span>
              </button>
              <button className={"bni bni-requests " + (tab === "requests" ? "on" : "")}
                      onClick={function() { switchTab("requests"); }}>
                <IconInbox/>
                {hostUnread > 0 && <span className="bni-badge">{hostUnread > 9 ? "9+" : hostUnread}</span>}
                <span>Requests</span>
              </button>
              <button className={"bni " + (tab === "profile" ? "on" : "")}
                      onClick={function() { switchTab("profile"); }}>
                <IconUser/>
                <span>Profile</span>
              </button>
            </>
          ) : (
            <>
              <button className={"bni " + (tab === "explore" ? "on" : "")}
                      onClick={function() { switchTab("explore"); }}>
                <span className="bni-icon-wrap">
                  <img src={navExplore} alt="" className="bni-img"/>
                </span>
                <span>Explore</span>
              </button>
              <button className={"bni " + (tab === "wishlist" ? "on" : "")}
                      onClick={function() { switchTab("wishlist"); }}>
                <span className="bni-icon-wrap">
                  <img src={navWishlist} alt="" className="bni-img"/>
                </span>
                <span>Wishlist</span>
              </button>
              <button className={"bni " + (tab === "profile" ? "on" : "")}
                      onClick={function() { switchTab("profile"); }}>
                <span className="bni-icon-wrap">
                  <img src={navProfile} alt="" className="bni-img"/>
                </span>
                <span>Profile</span>
              </button>
            </>
          )}
        </nav>
      )}

      {/* DARK FOOTER */}
      {!detail && (
        <footer className="footer">
          <div className="footer-in">
            <div className="footer-logo">
              <img src={kundaLogo} alt="" className="footer-logo-img"/>
              <span className="footer-logo-wordmark">KUNDA</span>
            </div>
            <div className="footer-tag">Built in The Gambia 🇬🇲</div>
            <div className="footer-links">
              <button className="footer-link" onClick={function() { openPolicy("terms"); }}>Terms</button>
              <span className="footer-dot">·</span>
              <button className="footer-link" onClick={function() { openPolicy("privacy"); }}>Privacy</button>
              <span className="footer-dot">·</span>
              <button className="footer-link" onClick={function() { openPolicy("help"); }}>Help Center</button>
            </div>
            <div className="footer-fine">
              The trusted way to find short-stay homes in The Gambia.
            </div>
          </div>
        </footer>
      )}

      {/* TOAST */}
      {toast && (
        <div className="toast">
          <IconCheck/> {toast}
        </div>
      )}
    </div>
  );
}
